import {
  ApartmentOutlined,
  BusinessOutlined,
  ChevronLeftRounded,
  ChevronRightRounded,
  CurrencyExchangeOutlined,
  DoDisturbOutlined,
  ExpandMoreOutlined,
  FactCheckOutlined,
  Functions as SigmaIcon,
  GavelOutlined,
  HandshakeOutlined,
  HomeWorkOutlined,
  InfoOutlined,
  LinkOutlined,
  ListAltOutlined,
  LockOutlined,
  OpenInNewRounded,
  PeopleAltOutlined,
  PercentOutlined,
  PointOfSaleOutlined,
  SavingsOutlined,
  ShowChartOutlined,
  SwapHorizOutlined,
  TaskAltOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import { grey } from "@mui/material/colors";
import arrayMutators from "final-form-arrays";
import { get, isNil } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { Field, Form, useField, useForm, useFormState } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";

import {
  dateOnlyKey,
  daysUntilDateOnly,
  formatDate,
} from "../../../../_src_shared/utils/functions/formatDate";
import RffFieldComponentRenderer from "../../../../components/finalForm/shared/RffFieldComponentRenderer";
import SearchInput from "../../../../components/inputs/SearchInput";
import PendingChangesBar from "../../../../components/common/PendingChangesBar";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import BorrowerExperienceEditor, {
  experienceFieldPaths,
} from "../../../../components/sidebar/LoanBorrowerSidebar/BorrowerExperienceEditor";
import { useCorrespondentOptions } from "../../../../hooks/useCorrespondentOptions";
import { useIsAcquisitions } from "../../../../pipelines/PipelineContext";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { loanPipelineClear } from "../../../../redux/actions/loanPipelineActions";
import { docUploadModalOpen } from "../../../../redux/actions/docUploadModalActions";
import useAssociationUtils from "../../../../components/associations/hooks/useAssociationUtils";
import LoanDocumentManagerTable from "../LoanDrilldownDocumentsTab/LoanDocumentManagerTable";
import useLocalStorageState from "use-local-storage-state";
import { nsKey } from "../../../../utils/storage/partnerStorage";
import LoanDrilldownDetailsTab from "../LoanDrilldownDetailsTab/LoanDrilldownDetailsTab";
import { LoanTabCanvas, LoanTabHeader } from "../../shared/tabLayout";
import {
  ACQUISITION_STAGES,
  StageTracker,
} from "../LoanDrilldownSummaryTab/summaryShared";
import { selectOptionsAmortizationType } from "../../../../constants/selectOptions/selectOptionsAmortizationType";
import { selectOptionsEstimatedFico } from "../../../../constants/selectOptions/selectOptionsEstimatedFico";
import { selectOptionsHousingEvents } from "../../../../constants/selectOptions/selectOptionsHousingEvents";
import { selectOptionsPropertyConditionAtOrigination } from "../../../../constants/selectOptions/selectOptionsPropertyConditionAtOrigination";
import { selectOptionsPropertyStatusAtOrigination } from "../../../../constants/selectOptions/selectOptionsPropertyStatusAtOrigination";
import { selectOptionsPropertyType } from "../../../../constants/selectOptions/selectOptionsPropertyType";
import {
  EXIT_STRATEGY_HIDDEN_PRODUCT_TYPES,
  getExitStrategyOptions,
  isExitStrategyHidden,
} from "../../../../constants/selectOptions/selectOptionsExitStrategy";
import { selectOptionsInterestCalcType } from "../../../../constants/selectOptions/selectOptionsInterestCalcType";
import { selectOptionsLoanProductType } from "../../../../constants/selectOptions/selectOptionsLoanProductType";
import { selectOptionsLoanPurpose } from "../../../../constants/selectOptions/selectOptionsLoanPurpose";
import { selectOptionsPipelineType } from "../../../../constants/selectOptions/selectOptionsPipelineType";
import { selectOptionsPrepaymentPenaltyStructure } from "../../../../constants/selectOptions/selectOptionsPrepaymentPenaltyStructure";
import { selectOptionsVomPaymentHistory } from "../../../../constants/selectOptions/selectOptionsVomPaymentHistory";
import { selectOptionsWireStatus } from "../../../../constants/selectOptions/selectOptionsWireStatus";
import { selectOptionsClosingStatus } from "../../../../constants/selectOptions/selectOptionsClosingStatus";
import { formatDollar } from "../../../../utils/numbers/formatDollar";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import { amber, status as colorStatus } from "../../../../theme/colors";
import { usePermissionsHook } from "../../../../hooks/usePermissionsHook";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";
import {
  CASH_OUT_REFI,
  DELAYED_PURCHASE_FIELD,
  DELAYED_PURCHASE_HINT,
  DELAYED_PURCHASE_LABEL,
  canBeDelayedPurchase,
  isDelayedPurchase,
  isPurchaseLike,
  normalizeLegacyDelayedPurchase,
} from "../../../../utils/loans/delayedPurchase";
import {
  ClassificationOverrideButton,
  ClassificationOverrideInline,
} from "../../../../components/borrowerClassification/BorrowerClassificationOverride";

/**
 * LoanDrilldownLoanInfoRedesignTab — restructured Loan Information tab.
 *
 * Layout: sticky left section-nav + right cards (one per section). Each row
 * speaks one "field language":
 *   • EDITABLE   → a bordered white input box (cyan focus ring).
 *   • BOOLEAN    → a Yes/No switch.
 *   • CALCULATED → an ƒx chip + value; hover shows the plain-english formula.
 *   • SYSTEM     → a lock + muted value (read-only, set automatically).
 *
 * Field classification + product/purpose visibility follow
 * `docs/loan-field-reference.md` (mirrors the backend Pydantic models):
 *   • Calc fields are NEVER bound to writable controls (§8.1).
 *   • Leverage metrics hide on null — null means N/A, not 0 (§8.2).
 *   • Construction / DSCR / Refinance / Purchase gates per §8.3–8.5.
 *   • Floating-rate + other value-conditional sub-fields per §8.6.
 *   • Dead Deal renders ONLY when loanStatus === 'Dead' (§1.1).
 *   • Box link fields are SYS — rendered read-only (§1.5).
 *   • verifiedLiquidity / verifiedExperienceTotal are router-written
 *     rollups — rendered read-only (§3.4, "do not hand-edit").
 *   • Fields on the PRICING_IMPACT_FIELDS drift list show a lock warning
 *     when the loan is rate-locked (§6.1).
 */

/* ── product / purpose predicates (docs/loan-field-reference.md) ── */
/* Delayed purchase is a FLAG on a cash-out refinance now (REQ-106), and it
   reads exactly as the retired "Delayed Purchase" purpose did: purchase-like,
   and NOT a refinance (no payoff / existing-loan rows — there is no debt). */
const isRefiPurpose = (p, delayed) =>
  typeof p === "string" &&
  p.startsWith("Refinance") &&
  !isDelayedPurchase(p, delayed);
const isPurchasePurpose = (p, delayed) => isPurchaseLike(p, delayed);
const isDSCR = (pt) => pt === "DSCR";
const RTL_TYPES = [
  "Fix and Flip",
  "Ground Up Construction",
  "Stabilized Bridge",
];
const isRTL = (pt) => RTL_TYPES.includes(pt);
// Construction products — totalHoldback, ARV, budget + LTC/LTARV apply (§8.3).
const CONSTRUCTION_TYPES = [
  "Fix and Flip",
  "Ground Up Construction",
  "Other",
  "Vacant Land",
];
const isConstruction = (pt) => CONSTRUCTION_TYPES.includes(pt);
// Interest reserves show on every product except DSCR (owner's DSCR display
// rules, dscr-drilldown-display-rules.md). Requested by Shyrose (2026-10-08).
const isInterestReserve = (pt) => !isDSCR(pt);
// "Multifamily Value Add" also carries a rehab/value-add holdback even though it
// isn't a construction product for the other gates (ARV/budget/LTC). Requested
// by Jordan.
const isMultifamilyValueAdd = (pt) => pt === "Multifamily Value Add";

/* ────── conditional-visibility rules (admin "show hidden fields" mode) ──────
   A row gated behind a product / purpose / value check carries a RULE rather
   than a bare boolean: `on` still decides whether the row renders normally,
   and `clauses` say WHY in plain english — so an admin who flips the reveal
   toggle can hover a revealed row and read the condition that hides it.

   Clause shapes (rendered by clauseText):
     { label, values }    → "Loan Product Type is one of the following values: …"
     { label, notValues } → "Loan Product Type is not one of …"
     { label, phrase }    → "Loan Purpose is a Refinance"
     { text }             → free-form sentence (not a loan field)

   all(…) ANDs rules together and concatenates their clauses. ── */
const when = (on, label, values) => ({
  on: !!on,
  clauses: [{ label, values: [].concat(values) }],
});
const whenNot = (on, label, notValues) => ({
  on: !!on,
  clauses: [{ label, notValues: [].concat(notValues) }],
});
const whenIs = (on, label, phrase) => ({
  on: !!on,
  clauses: [{ label, phrase }],
});
const whenText = (on, text) => ({ on: !!on, clauses: [{ text }] });
const all = (...rules) => ({
  on: rules.every((r) => r.on),
  clauses: rules.flatMap((r) => r.clauses),
});
/* a keep() argument, normalized — plain booleans still work (no explanation) */
const asRule = (c) =>
  c && typeof c === "object" && "on" in c ? c : { on: !!c, clauses: [] };

/* Builds the `keep` every section builder uses. Normally a field whose
   condition is false is dropped; in reveal mode it is KEPT and tagged with the
   rule, so the row renders tinted with its condition in a hover tooltip. */
const makeKeep = (reveal) => (cond, field) => {
  const r = asRule(cond);
  if (r.on) return [field];
  if (!reveal) return [];
  return [{ ...field, hiddenRule: r.clauses }];
};

/* Acquisitions overrides the pipeline enum on LoanAcquisitionBaseModel —
   verbatim from the model, NOT the preclose selectOptionsPipelineType list. */
const ACQ_PIPELINE_OPTIONS = ["Scenario", "Acquisitions", "Boarded"];

/* ── §6.1 pricing-drift watch list: editing these on a locked loan flips the
   lock to needs_pricing_review, so the row grows a warning hint. ── */
const PRICING_IMPACT_FIELDS = new Set([
  "baseLoanAmount",
  "totalLoanAmount",
  "totalHoldback",
  "totalInterestReserves",
  "loanProductType",
  "loanPurpose",
  "termMonths",
  "amortizationType",
  "interestRate",
  "discountPoints",
  "originationPoints",
  "extensionFeePoints",
  "exitFeePoints",
  "dscr",
  "prepaymentPenaltyMonths",
  "prepaymentPenaltyStructure",
]);
const LOCK_DRIFT_HINT =
  "This loan is rate-locked. Changing this field sends the lock back to pricing review.";

/* ── read-only value formatting for calculated fields ── */
const fmtCalc = (type, v) => {
  if (v === null || v === undefined || v === "") return null;
  switch (type) {
    case "dollar": {
      const n = Number(v);
      return Number.isFinite(n) ? formatDollar(n) : String(v);
    }
    // Cents-precision dollars (formatDollar rounds to whole dollars). Used where
    // the exact figure matters, e.g. the header Total loan.
    case "dollar2": {
      const n = Number(v);
      return Number.isFinite(n)
        ? `$${n.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : String(v);
    }
    case "percent": {
      const n = Number(v);
      return Number.isFinite(n) ? formatPercent(n) : String(v);
    }
    case "date":
      return formatDate(v) || String(v);
    default:
      return String(v);
  }
};

/* ───────────────────────── section config ─────────────────────────
   Built from live form values so value-conditional fields (floating rate,
   closing scheduled, loanStatus === 'Dead', existingLoanInDefault) and
   product/purpose gating react live.

   Field shape:
     { name, label, type, options?, disabled?, tip?, hint? } → editable (or
        read-only System when disabled) row
     { name, label, type, calc:true, formula, hideWhenNil? } → ƒx calc row
*/
const buildSections = (
  v,
  canEditUw,
  correspondentOptions = [],
  isAcq = false,
  reveal = false,
) => {
  const pt = v?.loanProductType;
  const purpose = v?.loanPurpose;
  const delayed = v?.[DELAYED_PURCHASE_FIELD];
  const refi = isRefiPurpose(purpose, delayed);
  const dscr = isDSCR(pt);
  const rtl = isRTL(pt);
  const construction = isConstruction(pt);
  const isDead = v?.loanStatus === "Dead";
  // Acquisitions (postclose) buy loans that ALREADY closed at the correspondent,
  // so the preclose scheduling block (cutoff / scheduled / closing status /
  // FundingShield / closing month-quarter-week) does not exist on
  // LoanAcquisitionBaseModel — it carries loanClosed + originationDate from
  // LoanBaseModel plus settlement/wire/boarding dates of its own. Some
  // acquisitions are locked BEFORE they close and stay loanClosed=False until
  // the note is signed, which is the only case expectedClosingDate applies.
  const acqClosed = isAcq && !!(v?.loanClosed || v?.originationDate);

  const keep = makeKeep(reveal);

  /* Plain-english rules behind each conditional row (see makeKeep). Values are
     the model enum strings verbatim — they are what the gate compares. */
  const SUB_STATUS_STATUSES = ["Processing", "Clear To Close", "Closed"];
  const ACQ_EXIT_STATUSES = ["Kicked", "Declined"];
  const R = {
    dscr: when(dscr, "Loan Product Type", "DSCR"),
    rtl: when(rtl, "Loan Product Type", RTL_TYPES),
    rtlOrConstruction: when(rtl || construction, "Loan Product Type", [
      ...new Set([...RTL_TYPES, ...CONSTRUCTION_TYPES]),
    ]),
    holdbackProducts: when(
      construction || isMultifamilyValueAdd(pt),
      "Loan Product Type",
      [...CONSTRUCTION_TYPES, "Multifamily Value Add"],
    ),
    refi: whenIs(
      refi,
      "Loan Purpose",
      "is a Refinance (not a delayed purchase)",
    ),
    cashOut: when(canBeDelayedPurchase(purpose), "Loan Purpose", CASH_OUT_REFI),
    // negative gate — exit strategy is N/A on these products, not just empty
    exitStrategy: whenNot(
      !isExitStrategyHidden(pt),
      "Loan Product Type",
      EXIT_STRATEGY_HIDDEN_PRODUCT_TYPES,
    ),
    canEditUw: whenText(
      canEditUw,
      "You have underwriting / admin edit access.",
    ),
    preclose: whenText(
      !isAcq,
      "The loan is in a pre-close pipeline (not Acquisitions).",
    ),
    acq: whenText(isAcq, "The loan is in the Acquisitions pipeline."),
    notAcqClosed: whenText(
      !acqClosed,
      "The loan has not closed at the correspondent yet.",
    ),
    acqClosed: whenText(
      acqClosed,
      "Loan Closed? is Yes (the loan closed at the correspondent).",
    ),
    dead: when(isDead, "Loan Status", "Dead"),
    subStatusStatus: when(
      SUB_STATUS_STATUSES.includes(v?.loanStatus),
      "Loan Status",
      SUB_STATUS_STATUSES,
    ),
    acqExitStatus: when(
      ACQ_EXIT_STATUSES.includes(v?.loanStatus),
      "Loan Status",
      ACQ_EXIT_STATUSES,
    ),
    inDefault: whenIs(
      !!v?.existingLoanInDefault,
      "Existing Loan In Default",
      "is Yes",
    ),
    completionGuaranty: whenIs(
      !!v?.isConstructionCompletionGuaranty,
      "Construction Completion Guaranty",
      "is Yes",
    ),
  };

  /* 1 ── Identity & Status — what/who this loan is */
  const identityFields = [
    { name: "loanNumber", label: "Loan Number", disabled: true },
    // stacked: label above a full-width input — long free text needs the room.
    { name: "loanName", label: "Loan Name", type: "text", stacked: true },
    { name: "sync_loan_id", label: "External Loan Sync ID", type: "text" },
    {
      name: "loanProductType",
      label: "Loan Product Type",
      type: "select",
      options: selectOptionsLoanProductType,
      hint: "Drives which fields, cards and leverage metrics apply to this loan.",
    },
    {
      name: "loanPurpose",
      label: "Loan Purpose",
      type: "select",
      options: selectOptionsLoanPurpose,
      hint: "Purchase vs Refinance gates the payoff / acquisition field sets.",
    },
    // Delayed purchase — a cash-out refi flag (REQ-106); the backend clears it
    // on any other purpose, and the save sends it alongside loanPurpose.
    ...keep(R.cashOut, {
      name: DELAYED_PURCHASE_FIELD,
      label: DELAYED_PURCHASE_LABEL,
      type: "booleanToggle",
      hint: DELAYED_PURCHASE_HINT,
    }),
    {
      name: "pipeline",
      label: "Current Pipeline",
      type: "select",
      // Acquisitions overrides the enum on the model (Scenario / Acquisitions /
      // Boarded) — the preclose list has none of those values.
      options: isAcq ? ACQ_PIPELINE_OPTIONS : selectOptionsPipelineType,
    },
    // loanStatus is SYS-driven (mark_loan_closed / mark_file_dead / SF sync) —
    // shown read-only here; the actions own the transitions (§1.1).
    {
      name: "loanStatus",
      label: "Loan Status",
      type: "text",
      disabled: true,
      tip: isAcq
        ? `Trade milestone (${ACQUISITION_STAGES.map((s) => s.key).join(" → ")}; off-ramps Kicked / Declined; Inactive = on hold, synced with the Salesforce On Hold flag) — changed from the Status pill in the loan header.`
        : "Set by loan actions (mark closed, mark dead) and Salesforce sync.",
    },
    // Acquisitions: subStatus carries the exact Salesforce path stage (and, on
    // an Inactive loan, the stage it was held at and resumes to).
    ...keep(R.acq, {
      name: "subStatus",
      label: "Salesforce Stage",
      type: "text",
      disabled: true,
      tip: "The Salesforce path stage this loan is at. While the loan is Inactive (on hold) it is the stage it was held at, and Resume returns the loan there.",
    }),
    // subStatus only applies to these statuses (§1.1) — none of which exist on
    // the acquisition milestone enum.
    ...keep(all(R.preclose, R.subStatusStatus), {
      name: "subStatus",
      label: "Sub Status",
      type: "text",
      disabled: true,
      tip: "Granular tracking within the current milestone (e.g. Appraisal Ordered, Appraisal In, Pre-Underwriting, Underwriting Review). Read-only here — it moves with the underwriting workflow, not by hand.",
    }),
    {
      name: "entityName",
      label: "Entity Name",
      type: "text",
      calc: true,
      formula: "Resolved from the borrowing entity in entities[].",
    },
    {
      name: "createDateTime",
      label: "Create Date Time",
      type: "date",
      disabled: true,
    },
    {
      name: "updateDateTime",
      label: "Update Date Time",
      type: "date",
      disabled: true,
    },
  ];

  /* 2 ── Amount & Structure — size + shape of the loan */
  const structureFields = [
    { name: "baseLoanAmount", label: "Base Loan Amount", type: "dollar" },
    // Construction products (§1.2) plus Multifamily Value Add (per Jordan) —
    // hidden for DSCR / Stabilized Bridge.
    ...keep(R.holdbackProducts, {
      name: "totalHoldback",
      label: "Total Holdback",
      type: "dollar",
    }),
    {
      name: "totalLoanAmount",
      label: "Total Loan Amount",
      type: "dollar",
      calc: true,
      formula: "Base Loan Amount + Total Holdback.",
    },
    // TEMPORARY (2026-10-21) — a manual entry point for Original UPB while it
    // is set on a test loan; remove once done. Normally it is written from the
    // Note extraction. Acquisitions only (LoanAcquisitionBaseModel field), and
    // only in the super-admin "Show conditionally hidden fields" mode — its
    // rule is never on, so keep() drops it unless the reveal toggle is active.
    ...(isAcq
      ? keep(
          whenText(
            false,
            "Temporary manual entry. Original UPB is normally filled in from the Note.",
          ),
          {
            name: "originalUPB",
            label: "Original UPB",
            type: "dollar",
            hint: "The loan amount when the correspondent closed it. Usually filled in from the Note.",
          },
        )
      : []),
    { name: "termMonths", label: "Term Months", type: "number" },
    // Mainly DSCR; UW/admin edit only (§1.2).
    ...keep(R.dscr, {
      name: "amortizationType",
      label: "Amortization Type",
      type: "select",
      options: selectOptionsAmortizationType,
      disabled: !canEditUw,
      tip: "Underwriting / admin edit only.",
    }),
    {
      name: "interestCalcType",
      label: "Interest Calc Type",
      type: "select",
      options: selectOptionsInterestCalcType,
      disabled: !canEditUw,
      tip: "Underwriting / admin edit only.",
    },
  ];

  /* 3 ── Pricing & Rate — rate, points and fee points.
     RTL fee points live here (not in a Prepayment card): exit/extension fees
     are the RTL analogue of DSCR prepay penalties (§1.2). */
  const pricingFields = [
    { name: "interestRate", label: "Interest Rate", type: "percent" },
    // Buy rate (RTL / GUC). Interim: saved to the loan with the rest of the
    // card. It is a real field on LoanAcquisitionBaseModel, but NOT on
    // UnderwritingBaseModel — preclose it rides LOSBase extra="allow", and the
    // backend's own preclose buy rate (LoanTerms.buyRate / the lock's
    // lock_buy_rate) does not read it yet.
    ...keep(R.rtl, { name: "buyRate", label: "Buy Rate", type: "percent" }),
    { name: "discountPoints", label: "Discount Points", type: "percent" },
    { name: "originationPoints", label: "Origination Points", type: "percent" },
    // Model key is exitFeePoints (§1.2) — exitFeePercent doesn't exist.
    ...keep(R.rtl, {
      name: "exitFeePoints",
      label: "Exit Fee Points",
      type: "percent",
    }),
    ...keep(R.rtl, {
      name: "extensionFeePoints",
      label: "Extension Fee Points",
      type: "percent",
    }),
    // Dutch interest — meaningful for RTL / construction (§1.2).
    ...keep(R.rtlOrConstruction, {
      name: "dutchInterest",
      label: "Dutch Interest?",
      type: "booleanCheckbox",
    }),
    {
      name: "floatingRateLoan",
      label: "Floating Rate Loan?",
      type: "booleanCheckbox",
    },
    ...(v?.floatingRateLoan
      ? [
          {
            name: "floatingRateLoanBaseRate",
            label: "Floating Rate Base Rate",
            type: "percent",
          },
          {
            name: "floatingRateLoanFloorRate",
            label: "Floating Rate Floor Rate",
            type: "percent",
          },
          {
            name: "floatingRateLoanCapRate",
            label: "Floating Rate Cap Rate",
            type: "percent",
          },
          {
            name: "floatingRateLoanRateIndex",
            label: "Floating Rate Index",
            type: "text",
          },
          {
            name: "floatingRateLoanRateMargin",
            label: "Floating Rate Margin",
            type: "percent",
          },
        ]
      : []),
  ];

  /* 4 ── Leverage — all auto-calculated; each hides when N/A (null) for this
     (product, purpose) per LEVERAGE_METRICS_BY_PRODUCT. */
  const leverageFields = [
    {
      name: "baseLoanToAsIsValue",
      label: "LTAIV",
      type: "percent",
      calc: true,
      hideWhenNil: true,
      formula: "Base Loan Amount ÷ Total As-Is Value.",
    },
    {
      name: "baseLoanToPurchasePrice",
      label: "LTPP",
      type: "percent",
      calc: true,
      hideWhenNil: true,
      formula: "Base Loan Amount ÷ Total Purchase Price.",
    },
    {
      name: "totalLoanToArv",
      label: "LTARV",
      type: "percent",
      calc: true,
      hideWhenNil: true,
      formula: "Total Loan Amount ÷ Total ARV.",
    },
    {
      name: "baseLoanToCost",
      label: "LTC",
      type: "percent",
      calc: true,
      hideWhenNil: true,
      formula:
        "Total Loan Amount ÷ Total Project Cost (cost basis + construction budget).",
    },
  ];

  /* 5 ── Payments & Reserves — schedule + reserve/liquidity requirements.
     Preclose derives the schedule dates from closing + term; a PURCHASED loan
     carries them verbatim off the note (stored, not derived — see
     loan_acquisitions.firstPaymentDate / maturityDate), so postclose they are
     editable rows instead of ƒx rows. requiredInterestCoverage /
     requiredLiquidity are UnderwritingBaseModel-only — hidden postclose. */
  const reserveFields = [
    isAcq
      ? {
          name: "firstPaymentDate",
          label: "First Payment Date",
          type: "date",
          hint: "Per the note, as it closed at the correspondent.",
        }
      : {
          name: "firstPaymentDate",
          label: "First Payment Date",
          type: "date",
          calc: true,
          formula:
            "1st of the second month after closing (uses origination date if set).",
        },
    isAcq
      ? {
          name: "maturityDate",
          label: "Maturity Date",
          type: "date",
          hint: "Per the note — stored for a purchased loan, not derived from term.",
        }
      : {
          name: "maturityDate",
          label: "Maturity Date",
          type: "date",
          calc: true,
          formula:
            "Closing (or origination) date + Term Months, snapped to month-end + 1 day.",
        },
    ...keep(all(R.preclose, R.rtl), {
      name: "requiredInterestCoverage",
      label: "Required Interest Coverage",
      type: "dollar",
      hint: "Guideline: months of payments × monthly payment (3 mo for class A/B, 6 mo for C).",
    }),
    ...keep(all(R.preclose, R.rtl), {
      name: "requiredLiquidity",
      label: "Required Liquidity",
      type: "dollar",
      hint: "Guideline: interest coverage + non-financed rehab + (purchase only) down payment & closing costs.",
    }),
  ];

  /* 6 ── DSCR & Rents (DSCR loans only) */
  const dscrFields = [
    ...keep(R.dscr, {
      name: "dscr",
      label: "DSCR (counterparty)",
      type: "float",
    }),
    ...keep(R.dscr, {
      name: "interestOnlyMonths",
      label: "Interest-Only Months",
      type: "number",
    }),
    ...keep(R.dscr, {
      name: "totalInPlaceRents",
      label: "Total In-Place Rents",
      type: "dollar",
    }),
    ...keep(R.dscr, {
      name: "totalMarketRents",
      label: "Total Market Rents",
      type: "dollar",
    }),
  ];

  /* 7 ── Refinance — payoff of the existing loan (refi purpose).
     refinancingAssetId + vomPaymentHistory are UW/admin view+edit only (§1.2)
     — hidden entirely for other roles. */
  const refiFields = [
    ...keep(all(R.refi, R.canEditUw), {
      name: "refinancingAssetId",
      label: "Refinancing Asset Id",
      type: "text",
    }),
    ...keep(R.refi, {
      name: "outstandingLoanUpb",
      label: "Outstanding Loan UPB",
      type: "dollar",
    }),
    ...keep(R.refi, {
      name: "exisitingLender",
      label: "Existing Lender",
      type: "text",
    }),
    ...keep(all(R.refi, R.canEditUw), {
      name: "vomPaymentHistory",
      label: "VOM Payment History",
      type: "selectMultiple",
      options: selectOptionsVomPaymentHistory,
    }),
    ...keep(R.refi, {
      name: "existingLoanInDefault",
      label: "Existing Loan In Default?",
      type: "booleanCheckbox",
    }),
    ...keep(all(R.refi, R.inDefault), {
      name: "existingLoanDefaultType",
      label: "Existing Loan Default Type",
      type: "text",
    }),
  ];

  /* 8 ── Prepayment — penalty structure/months are a DSCR concern (§1.2);
     the card auto-hides for RTL, whose exit/extension points live in Pricing. */
  const prepayFields = [
    ...keep(R.dscr, {
      name: "prepaymentPenaltyStructure",
      label: "Prepayment Structure",
      type: "select",
      options: selectOptionsPrepaymentPenaltyStructure,
    }),
    ...keep(R.dscr, {
      name: "prepaymentPenaltyMonths",
      label: "Prepayment Penalty Months",
      type: "number",
    }),
  ];

  /* 9 ── Closing & Funding (PRECLOSE) — ordered scheduling-first, then
     wire/funding. Every field below is UnderwritingBaseModel-only; the
     acquisitions set is built separately in 9b. */
  const preCloseClosingFields = [
    {
      name: "expectedClosingDate",
      label: "Expected Closing Date",
      type: "date",
    },
    {
      name: "closingCutoffDate",
      label: "Closing Cutoff Date",
      type: "date",
      hint: "Contract / TOE deadline — alert when the expected closing date passes it.",
    },
    {
      name: "closingScheduled",
      label: "Closing Scheduled?",
      type: "booleanCheckbox",
    },
    ...(v?.closingScheduled
      ? [
          // A DATETIME on the model: the signing time drives the wire cutoff
          // and the readiness signal ("Scheduled closing date + time"), so the
          // clock is kept — closing-readiness.md §4.
          {
            name: "closingScheduledDate",
            label: "Closing Scheduled Date & Time",
            type: "datetime",
            hint: "Setting this marks the loan scheduled; the readiness signal needs the time.",
          },
        ]
      : []),
    {
      name: "closingStatus",
      label: "Closing Status",
      type: "select",
      options: selectOptionsClosingStatus,
    },
    {
      name: "termsAccepted",
      label: "Terms Accepted?",
      type: "booleanCheckbox",
    },
    ...(v?.termsAccepted
      ? [
          {
            name: "termsAcceptedDate",
            label: "Terms Accepted Date",
            type: "date",
          },
        ]
      : []),
    // UW/admin edit only (§1.3) — SYS-stamped at close/funding.
    {
      name: "originationDate",
      label: "Origination Date",
      type: "date",
      disabled: !canEditUw,
      tip: "Stamped at close (mark_loan_closed). Underwriting / admin edit only.",
    },
    {
      name: "fundedDate",
      label: "Funded Date",
      type: "date",
      disabled: !canEditUw,
      tip: "Stamped at funding. Underwriting / admin edit only.",
    },
    {
      name: "wireStatus",
      label: "Wire Status",
      type: "select",
      options: selectOptionsWireStatus,
    },
    // Also a datetime on the model (keeps its clock).
    { name: "wireSentDate", label: "Wire Sent Date & Time", type: "datetime" },
    {
      name: "fundingShieldStatus",
      label: "Funding Shield Status",
      type: "select",
      options: ["Not Submitted", "Submitted", "Received"],
    },
    // SYS — stamped when FundingShield is requested (§1.3).
    {
      name: "fundingShieldRequestedDate",
      label: "Funding Shield Requested Date",
      type: "date",
      disabled: true,
      tip: "Stamped automatically when FundingShield is requested.",
    },
    {
      name: "closingMonth",
      label: "Closing Month",
      type: "text",
      calc: true,
      formula:
        "Auto-derived from Expected Closing Date — the month, formatted MM-YYYY. Not editable; change Expected Closing Date to update it.",
    },
    {
      name: "closingQuarter",
      label: "Closing Quarter",
      type: "text",
      calc: true,
      formula:
        "Auto-derived from Expected Closing Date — the calendar quarter, formatted YYYYQX. Not editable; change Expected Closing Date to update it.",
    },
    {
      name: "closingWeek",
      label: "Closing Week",
      type: "text",
      calc: true,
      formula:
        "Auto-derived from Expected Closing Date — the ISO week number, formatted WW-YYYY. Not editable; change Expected Closing Date to update it.",
    },
  ];

  /* 9b ── Closing & Settlement (POSTCLOSE) — the acquisition's own lifecycle.
     Two distinct closings live on an acquisition and must not be conflated:
       • the CORRESPONDENT's close — loanClosed (Yes/No). Yes carries
         originationDate (note date) + fundedDate (the loan's own funding at the
         correspondent, required once closed); No carries expectedClosingDate.
       • OUR purchase — pricing → commitment → wire release → settlementDate
         (= the purchase funded date), then boarded to the servicer. fundedDate
         is never our wire (loan-acquisitions.md "Key dates", owner 2026-10-02). */
  const acqClosingFields = [
    {
      name: "loanClosed",
      label: "Loan Closed?",
      type: "booleanToggle",
      disabled: !canEditUw,
      tip: "True once the correspondent's loan actually closed (note signed). Underwriting / admin edit only.",
      hint: "Sellers can request exceptions while this is No.",
    },
    ...keep(R.acqClosed, {
      name: "originationDate",
      label: "Note Date (Origination)",
      type: "date",
      required: true,
      disabled: !canEditUw,
      tip: "The date the correspondent's loan documents were signed. Underwriting / admin edit only.",
    }),
    ...keep(R.acqClosed, {
      name: "fundedDate",
      label: "Funded Date",
      type: "date",
      required: true,
      disabled: !canEditUw,
      hint: "The date the correspondent funded the loan. Not our purchase wire, that is the Settlement Date.",
    }),
    // Only for loans locked BEFORE they close — once closed it is noise.
    ...keep(R.notAcqClosed, {
      name: "expectedClosingDate",
      label: "Expected Closing Date",
      type: "date",
      hint: "Only applies while the loan has not closed yet (locked prior to close).",
    }),
    { name: "pricingDate", label: "Trade Priced Date", type: "date" },
    { name: "commitmentDate", label: "Commitment Date", type: "date" },
    {
      name: "expectedSettlementDate",
      label: "Expected Settlement Date",
      type: "date",
      hint: "When we expect to settle (fund) the purchase. A soft target that moves.",
    },
    {
      name: "settlementDate",
      label: "Settlement Date (Purchase Funded)",
      type: "date",
      hint: "The firm settlement date, which is the day our purchase funded. Filled in automatically when the wire is released, if still empty.",
    },
    {
      name: "wireReleaseDate",
      label: "Wire Release Date",
      type: "date",
      hint: "Date the purchase wire is released (funds the acquisition).",
    },
    {
      name: "fundingWireAmount",
      label: "Funding Wire Amount",
      type: "dollar",
      hint: "Total wire to fund the purchase at settlement.",
    },
    { name: "boardingDate", label: "Boarded To Servicer", type: "date" },
  ];

  /* 9c ── Closed Package (POSTCLOSE, read-only mirror). The checklist itself is
     owned by the Closed Package tab — these rows are the at-a-glance status so
     Loan Info answers "is this closed loan actually complete?" without a second
     editor competing for the same fields. */
  const closedPackageFields = isAcq
    ? [
        {
          name: "closingPackageComplete",
          label: "Closed Package Complete?",
          type: "booleanToggle",
          disabled: true,
          tip: "Full closed-loan package received and reviewed. Set on the Closed Package tab.",
        },
        {
          name: "closingPackageReceivedDate",
          label: "Package Received Date",
          type: "date",
          disabled: true,
          tip: "Set on the Closed Package tab.",
        },
        {
          name: "noteReviewStatus",
          label: "Note Review Status",
          type: "text",
          disabled: true,
          tip: "Not Reviewed / In Review / Passed / Exception / Failed. Set on the Closed Package tab.",
        },
        {
          name: "noteReviewDate",
          label: "Note Reviewed Date",
          type: "date",
          disabled: true,
          tip: "Set on the Closed Package tab.",
        },
        {
          name: "noteReviewedBy",
          label: "Note Reviewed By",
          type: "text",
          disabled: true,
          tip: "Set on the Closed Package tab.",
        },
        {
          name: "termsValidated",
          label: "Note Terms Reconciled?",
          type: "booleanToggle",
          disabled: true,
          tip: "Extracted note terms reconcile to the trade tape. Set on the Closed Package tab.",
        },
        {
          name: "collateralReceived",
          label: "Collateral Received?",
          type: "booleanToggle",
          disabled: true,
          tip: "Original collateral / note received. Set on the Closed Package tab.",
        },
        {
          name: "assignmentChainComplete",
          label: "Assignment Chain Complete?",
          type: "booleanToggle",
          disabled: true,
          tip: "Chain of assignment / allonge is complete. Set on the Closed Package tab.",
        },
        {
          name: "allongeReceived",
          label: "Allonge Received?",
          type: "booleanToggle",
          disabled: true,
          tip: "Set on the Closed Package tab.",
        },
        {
          name: "titlePolicyReceived",
          label: "Title Policy Received?",
          type: "booleanToggle",
          disabled: true,
          tip: "Set on the Closed Package tab.",
        },
        {
          name: "recordedMortgageReceived",
          label: "Recorded Mortgage Received?",
          type: "booleanToggle",
          disabled: true,
          tip: "Set on the Closed Package tab.",
        },
        {
          name: "mortgageeClauseValidated",
          label: "Mortgagee Clause Validated?",
          type: "booleanToggle",
          disabled: true,
          tip: "Insurance + title confirmed to reference the closing entity's mortgagee clause. Set on the Closed Package tab.",
        },
      ]
    : [];

  const closingFields = isAcq ? acqClosingFields : preCloseClosingFields;

  /* 10 ── Underwriting — classification + structure decisions.
     Classification is RTL-only (§1.2). */
  const underwritingFields = [
    // Postclose diligence — the acquisition analogue of preclose underwriting
    // (dd_status_options on LoanAcquisitionBaseModel).
    ...keep(R.acq, {
      name: "dueDiligenceStatus",
      label: "Due Diligence Status",
      type: "select",
      options: [
        "Not Started",
        "In Progress",
        "Cleared",
        "Conditional",
        "Kicked",
      ],
    }),
    ...keep(R.acq, {
      name: "dueDiligenceVendor",
      label: "Due Diligence Vendor",
      type: "text",
      hint: "3rd-party diligence firm, if used.",
    }),
    ...keep(all(R.acq, R.acqExitStatus), {
      name: "kickReason",
      label: "Kick / Decline Reason",
      type: "stringMultiline",
    }),
    // Both pipelines classify RTL / Ground Up loans (backend 2026-10-01).
    ...keep(R.rtl, {
      name: "borrower_classification_calculated",
      label: "Borrower Classification (Calculated)",
      type: "text",
      disabled: true,
      tip: "Calculated from the RTL credit + experience scoring.",
      // No override yet → the Override button sits here, left of the lock.
      before: <ClassificationOverrideButton hideWhenSet label="Override" />,
    }),
    // Never an inline input: the override is set through the modal, which
    // posts the class together with its required justification note.
    ...keep(R.rtl, {
      name: "borrower_classification_override",
      label: "Borrower Classification (Override)",
      type: "text",
      disabled: true,
      // Only once an override exists; until then the button is on the
      // Calculated row above.
      hideWhenEmpty: true,
      render: () => <ClassificationOverrideInline />,
    }),
    {
      name: "closingAsEntity",
      label: "Closing As Entity?",
      type: "booleanToggle",
      hint: "Gates borrower entity-ownership % fields.",
    },
    ...keep(R.canEditUw, {
      name: "guidelineConforming",
      label: "Guideline Conforming?",
      type: "booleanCheckbox",
    }),
    ...keep(R.exitStrategy, {
      name: "exitStrategy",
      label: "Exit Strategy",
      type: "select",
      options: getExitStrategyOptions(pt, v?.exitStrategy),
    }),
    // Product-authoritative resolved FICO (highest-ownership guarantor + product
    // tie-break) — drives the hard-exception min-credit gate. UW-editable.
    ...keep(R.canEditUw, {
      name: "resolvedCreditScore",
      label: "Resolved Credit Score",
      type: "number",
      tip: "The loan's qualifying FICO — the highest-ownership guarantor's score, with the product tie-break (MAX for RTL/DSCR, MIN for Ground Up). Underwriting only.",
    }),
    ...keep(R.canEditUw, {
      name: "resolvedCreditScoreBasis",
      label: "Resolved Credit Score Basis",
      type: "text",
      tip: "One line on how the resolved score was determined — which guarantor, which tie-break, and the source (credit report / sizer / manual).",
    }),
    // Completion guaranty type — only relevant on construction loans that carry a
    // completion guaranty (conditional on isConstructionCompletionGuaranty). Values
    // copied verbatim from the backend enum.
    ...keep(R.completionGuaranty, {
      name: "completionGuarantyType",
      label: "Completion Guaranty Type",
      type: "select",
      options: ["Full", "Conditional"],
    }),
  ];

  /* 11 ── Dead Deal — ONLY when loanStatus === 'Dead' (§1.1). The values are
     written by mark_file_dead; keep them editable for post-mortem cleanup. */
  const deadFields = [
    ...keep(R.dead, {
      name: "dealDeadReason",
      label: "Deal Dead Reason",
      type: "text",
    }),
    ...keep(R.dead, {
      name: "dealDeadNotes",
      label: "Deal Dead Notes",
      type: "stringMultiline",
    }),
  ];

  /* 12 ── External Links — Box folder URLs. Normally written by the Box → LOS
     folder sync, but editable here (with an open-in-new-tab affordance). */
  const linkFields = [
    {
      name: "box_loan_folder_url",
      label: "Box — Loan Folder URL",
      type: "text",
      linkable: true,
      placeholder: "https://app.box.com/folder/…",
      tip: "Usually written by the Box → LOS folder sync; editable here.",
    },
    {
      name: "box_correspondent_folder_url",
      label: "Box — Correspondent Folder URL",
      type: "text",
      linkable: true,
      placeholder: "https://app.box.com/folder/…",
      tip: "Usually written by the Box → LOS folder sync; editable here.",
    },
  ];

  /* 13 ── Correspondent — the deal's correspondent company. Saves the id via
     update_loan_data, exactly like the drilldown header picker. */
  const correspondentFields = [
    {
      name: "correspondentCompanyId",
      label: "Correspondent",
      type: "selectAutocomplete",
      options: correspondentOptions,
    },
  ];

  // `zone` groups sections under the Deal terms / Pipeline zone headers.
  // `strip: true` renders as an inline computed strip instead of a card.
  return [
    // ── Deal terms — "what is this loan?" ──
    {
      id: "structure",
      zone: "terms",
      label: "Amount & Structure",
      icon: <GavelOutlined className="thin" />,
      fields: structureFields,
    },
    {
      id: "pricing",
      zone: "terms",
      label: "Pricing & Rate",
      icon: <PercentOutlined className="thin" />,
      fields: pricingFields,
    },
    {
      id: "dscr",
      zone: "terms",
      label: "DSCR & Rents",
      icon: <ApartmentOutlined className="thin" />,
      fields: dscrFields,
    },
    {
      id: "refi",
      zone: "terms",
      label: "Refinance",
      icon: <CurrencyExchangeOutlined className="thin" />,
      fields: refiFields,
    },
    {
      id: "reserves",
      zone: "terms",
      label: "Payments & Reserves",
      icon: <SavingsOutlined className="thin" />,
      fields: reserveFields,
    },
    {
      id: "prepay",
      zone: "terms",
      label: "Prepayment",
      icon: <PointOfSaleOutlined className="thin" />,
      fields: prepayFields,
    },
    {
      id: "leverage",
      zone: "terms",
      strip: true,
      label: "Leverage",
      icon: <ShowChartOutlined className="thin" />,
      fields: leverageFields,
    },
    // ── Pipeline — "where is it in the process?" ──
    {
      id: "identity",
      zone: "pipeline",
      label: "Identity & Status",
      icon: <InfoOutlined className="thin" />,
      fields: identityFields,
    },
    {
      id: "underwriting",
      zone: "pipeline",
      label: "Underwriting",
      icon: <FactCheckOutlined className="thin" />,
      fields: underwritingFields,
    },
    {
      id: "closing",
      zone: "pipeline",
      label: isAcq ? "Closing & Settlement" : "Closing & Funding",
      icon: <HandshakeOutlined className="thin" />,
      fields: closingFields,
    },
    {
      id: "closedPackage",
      zone: "pipeline",
      label: "Closed Package",
      icon: <TaskAltOutlined className="thin" />,
      fields: closedPackageFields,
    },
    {
      id: "dead",
      zone: "pipeline",
      label: "Dead Deal",
      icon: <DoDisturbOutlined className="thin" />,
      fields: deadFields,
    },
    // ── More ──
    {
      id: "correspondent",
      zone: "more",
      label: "Correspondent",
      icon: <BusinessOutlined className="thin" />,
      fields: correspondentFields,
    },
    {
      id: "links",
      zone: "more",
      label: "External Links",
      icon: <LinkOutlined className="thin" />,
      fields: linkFields,
    },
  ];
};

/* zone metadata — the headers above each cluster of loan cards */
const LOAN_ZONES = [
  { id: "terms", label: "Deal terms", hint: "what is this loan?" },
  { id: "pipeline", label: "Pipeline", hint: "where is it in the process?" },
  { id: "more", label: "More", hint: null },
];

/* ═══════════════════ borrower / property field config ═══════════════════
   `name` is a NESTED path into the form values (`borrowers[i].firstName`).
   UnifiedEditForm partitions dirty nested paths on save and routes each
   entity's changed fields to updateBorrower / updateSubjectProperty.

   Both builders now take a ctx so the doc's product/purpose + value gates
   apply: { rtl, dscr, construction, purchase, refi, closingAsEntity, loan }.
*/

const OPT_CITIZENSHIP = [
  "US Citizen",
  "Foreign National with SSN",
  "Foreign National",
  "Foreign National Non-Resident",
  "Other - Options do not apply",
];
const OPT_MARITAL = ["Married", "Not Married"];
const OPT_ID_TYPE = [
  "Driver License",
  "Passport",
  "State ID",
  "Military ID",
  "Other",
];
const OPT_MORTGAGE_LATES = [
  "1x30x12",
  "1x60x24",
  "1x90",
  "2x30x12",
  "2x60x24",
  "2x90",
  "3x30x12",
  "3x60x24",
  "3x90",
  "4+ 30-day lates",
  "4+ 60-day lates",
  "4+ 90-day lates",
];

const borrowerDisplayName = (b, i) => {
  const name = [b?.firstName, b?.lastName].filter(Boolean).join(" ").trim();
  return name || b?.company || b?.emailAddress || `Borrower ${i + 1}`;
};

const propertyDisplayName = (p, i) =>
  addressLabel(p?.address) || p?.stringAddressFinalTitle || `Property ${i + 1}`;

const buildBorrowerSections = (i, ctx) => {
  const n = (name) => `borrowers[${i}].${name}`;
  const { rtl, dscr, closingAsEntity, loan, reveal } = ctx;
  const keep = makeKeep(reveal);
  /* plain boolean gate — these produce experience-category STRINGS, not field
     objects, so they are never revealed (the editor keys off the real list). */
  const keepFlag = (cond, flag) => (cond ? [flag] : []);
  // Credit lifecycle (§3.3): estimated FICO matters pre-pull; once a credit
  // report lands, the real trio is the source of truth.
  const hasCreditReport = !isNil(get(loan, n("creditReportDate")));

  /* Plain-english rules behind each conditional borrower row (see makeKeep). */
  const R = {
    rtl: when(rtl, "Loan Product Type", RTL_TYPES),
    noCreditReport: whenText(
      !hasCreditReport,
      "No credit report has been pulled for this borrower yet (Credit Report Date is empty).",
    ),
    closingAsEntity: whenIs(closingAsEntity, "Closing As Entity", "is Yes"),
  };
  // Which self-reported experience categories this product exposes. Preserves
  // the gating the three hasExperienceWith* rows carried before they became
  // the editor's checkboxes: DSCR only ever showed income-producing.
  const experienceCategoryFlags = [
    ...keepFlag(rtl, "hasExperienceWithFixFlips"),
    ...keepFlag(rtl || dscr, "hasExperienceWithIncomeProducingProperties"),
    ...keepFlag(rtl, "hasExperienceWithGroundUpConstruction"),
  ];

  return [
    {
      id: "contact",
      label: "Contact & Identity",
      fields: [
        { name: n("firstName"), label: "First Name", type: "text" },
        { name: n("middleName"), label: "Middle Name", type: "text" },
        { name: n("lastName"), label: "Last Name", type: "text" },
        { name: n("emailAddress"), label: "Email Address", type: "email" },
        { name: n("phone"), label: "Phone", type: "phone" },
        { name: n("dateOfBirth"), label: "Date of Birth", type: "date" },
        {
          name: n("maritalStatus"),
          label: "Marital Status",
          type: "select",
          options: OPT_MARITAL,
        },
        {
          name: n("citizenshipStatus"),
          label: "Citizenship Status",
          type: "select",
          options: OPT_CITIZENSHIP,
          hint: "Anything other than US Citizen flags the borrower (and the loan) foreign-national.",
        },
        {
          name: n("borrowerIdType"),
          label: "ID Type",
          type: "select",
          options: OPT_ID_TYPE,
        },
        {
          name: n("idExpirationDate"),
          label: "ID Expiration Date",
          type: "date",
        },
        {
          name: n("borrowerHasSSN"),
          label: "Has a US-issued SSN?",
          type: "booleanCheckbox",
        },
        {
          name: n("borrowerSSN"),
          label: "Social Security Number",
          type: "ssn",
        },
        { name: n("company"), label: "Company", type: "text" },
      ],
    },
    {
      id: "credit",
      label: "Credit",
      fields: [
        // Pre-credit-pull estimates — hidden once a report exists (§3.3).
        ...keep(R.noCreditReport, {
          name: n("estimatedFICO"),
          label: "Estimated FICO Range",
          type: "select",
          options: selectOptionsEstimatedFico,
        }),
        ...keep(R.noCreditReport, {
          name: n("estimatedNumericFICO"),
          label: "Estimated Numeric FICO",
          type: "number",
          hint: "Preferred over the banded range for pricing until the credit pull.",
        }),
        { name: n("ficoLow"), label: "FICO Low", type: "number" },
        {
          name: n("ficoMid"),
          label: "FICO Mid",
          type: "number",
          hint: "The pricing / classification score.",
        },
        { name: n("ficoMax"), label: "FICO High", type: "number" },
        {
          name: n("creditReportDate"),
          label: "Credit Report Date",
          type: "date",
        },
        // SYS — stamped on credit-auth submission (§3.3).
        {
          name: n("creditAuthDate"),
          label: "Credit Auth Date",
          type: "date",
          disabled: true,
          tip: "Stamped when the credit authorization is submitted.",
        },
        {
          name: n("backgroundDate"),
          label: "Background Check Date",
          type: "date",
        },
        {
          name: n("mortgage_lates"),
          label: "Mortgage Lates",
          type: "selectMultiple",
          options: OPT_MORTGAGE_LATES,
        },
      ],
    },
    {
      id: "role",
      label: "Role & Ownership",
      fields: [
        // Only meaningful when the deal closes as an entity (§3.5). Stored as
        // a decimal fraction — the percent formatter handles display.
        ...keep(R.closingAsEntity, {
          name: n("entityPercentOwnership"),
          label: "Entity Ownership %",
          type: "percent",
        }),
        {
          name: n("isGuarantor"),
          label: "Is a guarantor?",
          type: "booleanCheckbox",
        },
        {
          name: n("isPrimaryContactForLoan"),
          label: "Primary contact for the loan?",
          type: "booleanCheckbox",
        },
        {
          name: n("repeatBorrowerFlag"),
          label: "Repeat borrower?",
          type: "booleanCheckbox",
        },
        { name: n("role"), label: "Role", type: "text" },
      ],
    },
    {
      id: "liquidity",
      label: "Liquidity",
      fields: [
        { name: n("cashLiquidity"), label: "Cash Liquidity", type: "dollar" },
        { name: n("otherLiquidity"), label: "Other Liquidity", type: "dollar" },
        {
          name: n("totalLiquidity"),
          label: "Total (Stated) Liquidity",
          type: "dollar",
          calc: true,
          formula: "Cash Liquidity + Other Liquidity.",
        },
        // Router-written rollup — do not hand-edit (§3.4).
        ...keep(R.rtl, {
          name: n("verifiedLiquidity"),
          label: "Verified Liquidity",
          type: "dollar",
          disabled: true,
          tip: "Rolled up from verified liquidity sources. Edit the sources, not this total.",
        }),
      ],
    },
    {
      id: "experience",
      // RTL-only qualification block (§3.4).
      label: "Experience",
      // Renders the self-reported category editor under this card's rows — the
      // same control the borrower fills out in the loan application. It owns
      // the three hasExperienceWith* flags (which used to be Yes/No rows here)
      // plus the six count buckets those flags gate. Omitted entirely when the
      // product gates every category away, so the heading never renders empty.
      ...(experienceCategoryFlags.length
        ? {
            experience: {
              prefix: `borrowers[${i}]`,
              categoryFlags: experienceCategoryFlags,
            },
          }
        : {}),
      fields: [
        ...keep(R.rtl, {
          name: n("isExperienced"),
          label: "Experienced Borrower?",
          type: "booleanCheckbox",
        }),
        // Router-written rollup — do not hand-edit (§3.4).
        ...keep(R.rtl, {
          name: n("verifiedExperienceTotal"),
          label: "Verified Experience Total",
          type: "number",
          disabled: true,
          tip: "Rolled up from validated experience records. Edit the records, not this total.",
        }),
        ...keep(R.rtl, {
          name: n("borrowerExperienceSummary"),
          label: "Experience Summary",
          type: "stringMultiline",
        }),
      ],
    },
    {
      id: "notes",
      label: "Notes",
      fields: [
        {
          name: n("additional_borrower_notes"),
          label: "Additional Borrower Notes",
          type: "stringMultiline",
        },
      ],
    },
  ];
};

const buildPropertySections = (i, ctx) => {
  const n = (name) => `subjectProperties[${i}].${name}`;
  const { construction, interestReserve, dscr, purchase, refi, loan, reveal } =
    ctx;
  const keep = makeKeep(reveal);
  const p = get(loan, `subjectProperties[${i}]`) || {};
  const listed = !!p.propertyListedForSale;
  const inFloodZone = !!p.floodZone;
  const mixedOrCommercial = /mixed|commercial/i.test(p.propertyType || "");

  /* Plain-english rules behind each conditional property row (see makeKeep). */
  const R = {
    construction: when(construction, "Loan Product Type", CONSTRUCTION_TYPES),
    interestReserve: whenNot(interestReserve, "Loan Product Type", "DSCR"),
    dscr: when(dscr, "Loan Product Type", "DSCR"),
    purchase: whenIs(
      purchase,
      "Loan Purpose",
      "is Purchase, or Refinance - Cash Out with Delayed purchase? Yes",
    ),
    refi: whenIs(
      refi,
      "Loan Purpose",
      "is a Refinance (not a delayed purchase)",
    ),
    listed: whenIs(listed, "Property Listed For Sale", "is Yes"),
    inFloodZone: whenIs(inFloodZone, "Flood Zone", "is Yes"),
    mixedOrCommercial: whenIs(
      mixedOrCommercial,
      "Property Type",
      "is a mixed-use or commercial type",
    ),
  };

  return [
    {
      id: "details",
      label: "Property Details",
      fields: [
        {
          name: n("stringAddressFinalTitle"),
          label: "Address on Final Title",
          type: "text",
        },
        {
          name: n("propertyType"),
          label: "Property Type",
          type: "select",
          options: selectOptionsPropertyType,
        },
        // Change-of-use / GUC only (§2.1).
        ...keep(R.construction, {
          name: n("proposedPropertyType"),
          label: "Proposed Property Type",
          type: "select",
          options: selectOptionsPropertyType,
        }),
        {
          name: n("propertyRuralIndicator"),
          label: "Rural area?",
          type: "booleanCheckbox",
        },
        {
          name: n("residentialSqFootage"),
          label: "Residential Sq Ft",
          type: "number",
        },
        ...keep(R.mixedOrCommercial, {
          name: n("commercialSqFootage"),
          label: "Commercial Sq Ft",
          type: "number",
        }),
        ...keep(R.construction, {
          name: n("afterRenoSqFootage"),
          label: "After-Reno Sq Ft",
          type: "number",
        }),
        {
          name: n("propertyConditionAtOrigination"),
          label: "Property Condition",
          type: "select",
          options: selectOptionsPropertyConditionAtOrigination,
        },
        {
          name: n("propertyStatusAtOrigination"),
          label: "Status at Origination",
          type: "selectMultiple",
          options: selectOptionsPropertyStatusAtOrigination,
        },
        ...keep(R.construction, {
          name: n("condoConversion"),
          label: "Condo conversion?",
          type: "booleanCheckbox",
        }),
        ...keep(R.construction, {
          name: n("changeOfUseFlag"),
          label: "Change of use?",
          type: "booleanCheckbox",
        }),
      ],
    },
    {
      id: "valuation",
      label: "Valuation & Cost Basis",
      fields: [
        // Purchase price stays visible on refi to carry history (§2.2).
        { name: n("purchasePrice"), label: "Purchase Price", type: "dollar" },
        { name: n("purchaseDate"), label: "Purchase Date", type: "date" },
        ...keep(R.purchase, {
          name: n("sellersConcession"),
          label: "Seller's Concession",
          type: "dollar",
        }),
        ...keep(R.purchase, {
          name: n("closingCosts"),
          label: "Closing Costs",
          type: "dollar",
        }),
        ...keep(R.purchase, {
          name: n("assignmentFees"),
          label: "Assignment Fees",
          type: "dollar",
        }),
        // Refi-only: seasoned/verified improvements (§2.2).
        ...keep(R.refi, {
          name: n("additionalBasis"),
          label: "Additional Basis",
          type: "dollar",
        }),
        { name: n("asIsValue"), label: "As-Is Value", type: "dollar" },
        ...keep(R.construction, {
          name: n("arv"),
          label: "ARV",
          type: "dollar",
        }),
        {
          name: n("secondaryAsIsValue"),
          label: "Secondary As-Is Value",
          type: "dollar",
        },
        ...keep(R.construction, {
          name: n("secondaryArv"),
          label: "Secondary ARV",
          type: "dollar",
        }),
        { name: n("appraisalDate"), label: "Appraisal Date", type: "date" },
        {
          name: n("secondaryAppraisalDate"),
          label: "Secondary Appraisal Date",
          type: "date",
        },
        {
          name: n("replacementCostEstimate"),
          label: "Replacement Cost Estimate",
          type: "dollar",
        },
        // ── The cost-basis waterfall → the LTC denominator (§2.4) ──
        {
          name: n("acquisitionCost"),
          label: "Acquisition Cost",
          type: "dollar",
          calc: true,
          formula:
            "Purchase Price + Assignment Fees + Closing Costs − Seller's Concession.",
        },
        {
          name: n("costBasis"),
          label: "Cost Basis",
          type: "dollar",
          calc: true,
          formula:
            "Acquisition Cost + Additional Basis (excludes the construction budget).",
        },
        ...keep(R.construction, {
          name: n("totalProjectCost"),
          label: "Total Project Cost",
          type: "dollar",
          calc: true,
          formula: "Cost Basis + Construction Budget — the LTC denominator.",
        }),
      ],
    },
    {
      id: "listing",
      label: "Listing",
      fields: [
        {
          name: n("propertyListedForSale"),
          label: "Currently listed for sale?",
          type: "booleanCheckbox",
        },
        // Sub-fields only when listed (§2.2).
        ...keep(R.listed, {
          name: n("lastListedForSaleDate"),
          label: "Last Listed Date",
          type: "date",
        }),
        ...keep(R.listed, {
          name: n("lastListedForSalePrice"),
          label: "Last Listed Price",
          type: "dollar",
        }),
      ],
    },
    {
      id: "flood",
      label: "Flood & Carrying Costs",
      fields: [
        {
          name: n("floodZone"),
          label: "In a flood zone?",
          type: "booleanCheckbox",
        },
        ...keep(R.inFloodZone, {
          name: n("floodCertDate"),
          label: "Flood Cert Date",
          type: "date",
        }),
        // Taxes + insurance apply to any escrowed loan; the rest are DSCR
        // expense underwriting (§2.3).
        { name: n("annualTaxes"), label: "Annual Taxes", type: "dollar" },
        {
          name: n("annualInsurance"),
          label: "Annual Insurance",
          type: "dollar",
        },
        ...keep(all(R.dscr, R.inFloodZone), {
          name: n("annualFloodInsurance"),
          label: "Annual Flood Insurance",
          type: "dollar",
        }),
        ...keep(R.dscr, {
          name: n("annualHoaFees"),
          label: "Annual HOA Fees",
          type: "dollar",
        }),
        ...keep(R.dscr, {
          name: n("annualPropertyMgmtFees"),
          label: "Annual Property Mgmt Fees",
          type: "dollar",
        }),
        ...keep(R.dscr, {
          name: n("annualOtherExpense"),
          label: "Annual Other Expenses",
          type: "dollar",
        }),
      ],
    },
    {
      id: "budget",
      // Construction-gated (§8.3). Non-construction, non-DSCR products keep
      // only the interest-reserve rows.
      label: construction ? "Construction & Budget" : "Interest Reserves",
      fields: [
        ...keep(R.construction, {
          name: n("constructionBudget"),
          label: "Construction Budget",
          type: "dollar",
        }),
        ...keep(R.construction, {
          name: n("financedBudgetPercent"),
          label: "Financed Budget %",
          type: "percent",
        }),
        ...keep(R.construction, {
          name: n("financedBudget"),
          label: "Financed Budget",
          type: "dollar",
          calc: true,
          formula:
            "Financed Budget % × Construction Budget (defaults to the full budget when no % is set).",
        }),
        ...keep(R.interestReserve, {
          name: n("interestReserves"),
          label: "Interest Reserves",
          type: "dollar",
        }),
        // Whether THIS property's interest reserve is financed from loan
        // proceeds (capitalized into UPB → feeds the loan's
        // capitalizedInterestReserve). Assume financed when a reserve is set;
        // the Note↔HUD tie-out validates the financed amount.
        ...keep(R.interestReserve, {
          name: n("financedReserve"),
          label: "Interest Reserve Financed?",
          type: "booleanToggle",
          tip: "Is this property's interest reserve financed from loan proceeds (capitalized into UPB)? Sums into the loan's Capitalized Interest Reserve; validated against the Note / HUD.",
        }),
        ...keep(R.construction, {
          name: n("budgetIsFinal"),
          label: "Budget Is Final?",
          type: "booleanCheckbox",
        }),
        ...keep(R.construction, {
          name: n("budgetMatchesAppraisal"),
          label: "Budget Matches Appraisal?",
          type: "booleanCheckbox",
        }),
        // SYS — set by the budget document ingest (§2.3).
        ...keep(R.construction, {
          name: n("finalBudgetUploaded"),
          label: "Final Budget Uploaded?",
          type: "booleanCheckbox",
          disabled: true,
          tip: "Set automatically when a final budget document is ingested.",
        }),
        ...keep(R.construction, {
          name: n("finalBudgetFeasible"),
          label: "Final Budget Feasible?",
          type: "booleanCheckbox",
        }),
      ],
    },
    {
      id: "notes",
      label: "Notes",
      fields: [
        {
          name: n("subjectPropertyNotes"),
          label: "Subject Property Notes",
          type: "stringMultiline",
        },
      ],
    },
  ];
};

/* ═════════════════════════ field language ═════════════════════════ */

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const T = {
  line: "#f1f5f9",
  label: "#475569",
  value: "#0f172a",
  muted: "#94a3b8",
  border: "#e2e8f0",
  borderHover: "#cbd5e1",
  focus: "#06b6d4",
  focusRing: "rgba(6,182,212,0.12)",
  chipBg: "#ecfeff",
  chipText: "#0e7490",
  chipRing: "#cffafe",
  warn: "#b45309",
};

/* ── one clause of a hidden-field rule, as a sentence ── */
const clauseText = (c) => {
  if (c.text) return c.text;
  if (c.phrase) return `${c.label} ${c.phrase}`;
  if (c.notValues)
    return c.notValues.length === 1
      ? `${c.label} is not ${c.notValues[0]}`
      : `${c.label} is not one of the following values: ${c.notValues.join(", ")}`;
  const values = c.values || [];
  if (values.length === 1) return `${c.label} is ${values[0]}`;
  return `${c.label} is one of the following values: ${values.join(", ")}`;
};

/* ── hover body for a revealed conditionally-hidden row ── */
const HiddenRuleTip = ({ clauses }) => {
  const list = clauses?.length
    ? clauses
    : [{ text: "a condition on this loan is met." }];
  return (
    <Box sx={{ fontSize: 11.5, lineHeight: 1.6, py: 0.25 }}>
      <Box sx={{ fontWeight: 700, mb: 0.5 }}>
        This field only displays when:
      </Box>
      {list.map((c, i) => (
        <Box key={i} sx={{ display: "flex", gap: "6px" }}>
          <Box component="span">{"•"}</Box>
          <Box component="span">{clauseText(c)}</Box>
        </Box>
      ))}
      {list.length > 1 && (
        <Box sx={{ mt: 0.5, opacity: 0.8 }}>All of the above must be true.</Box>
      )}
    </Box>
  );
};

/* Tint for a row that is normally hidden and only visible because an admin
   turned the reveal toggle on — amber (the product accent), not a status color,
   so it never reads as an error on the field. */
const HIDDEN_ROW_SX = {
  background: amber.light,
  borderLeft: `3px solid ${amber.main}`,
  borderRadius: "4px",
  px: 1,
  mx: -1,
  cursor: "help",
};

/* ── row scaffold: label left, control right, subtle bottom divider ── */
// `field` marks the row as a `?focus=` deep-link target (useDrilldownFieldFocus).
// `stacked` puts the label above a full-width control (set per field def).
const FieldRow = ({
  label,
  required,
  hint,
  warnHint,
  hiddenRule,
  field,
  stacked,
  children,
}) => {
  const row = (
    <Flx
      ac={!stacked}
      jc="space-between"
      g={stacked ? 0 : 2}
      data-drilldown-field={field}
      sx={{
        minHeight: 44,
        ...(stacked
          ? { flexDirection: "column", alignItems: "stretch", p: "10px 0 15px" }
          : null),
        borderBottom: `1px solid ${T.line}`,
        "&:last-of-type": { borderBottom: "none" },
        ...(hiddenRule
          ? {
              ...HIDDEN_ROW_SX,
              // the Tooltip wrapper below makes every revealed row the only
              // child of its own Box, so :last-of-type would otherwise strip
              // the divider from all of them.
              "&:last-of-type": { borderBottom: `1px solid ${T.line}` },
            }
          : null),
      }}
    >
      <Flx ac g={0.5} sx={{ minWidth: 0, py: 0.5 }}>
        {hiddenRule && (
          <VisibilityOffOutlined
            sx={{ fontSize: 14, color: amber.dark, flexShrink: 0 }}
          />
        )}
        <Txt
          sx={{ fontSize: 13, color: hiddenRule ? amber.chipText : T.label }}
        >
          {label}
          {required && (
            <Box
              component="span"
              aria-hidden="true"
              sx={{ color: colorStatus.error.fg, fontWeight: 500, ml: "3px" }}
            >
              *
            </Box>
          )}
        </Txt>
        {hint && (
          <Tooltip
            arrow
            placement="top"
            title={<Box sx={{ fontSize: 11.5, lineHeight: 1.5 }}>{hint}</Box>}
          >
            <InfoOutlined
              sx={{ fontSize: 14, color: "#cbd5e1", cursor: "help" }}
            />
          </Tooltip>
        )}
        {warnHint && (
          <Tooltip
            arrow
            placement="top"
            title={
              <Box sx={{ fontSize: 11.5, lineHeight: 1.5 }}>{warnHint}</Box>
            }
          >
            <LockOutlined
              sx={{ fontSize: 14, color: T.warn, cursor: "help" }}
            />
          </Tooltip>
        )}
      </Flx>
      <Box
        sx={{
          // The control area is ~half the row and scales with the card, so the
          // editable input sits at about 50% of the card width (right-aligned)
          // rather than a narrow fixed 260px. The input itself is width:100% of
          // this. `0 1 50%` = basis 50%, no grow past it, can shrink if a long
          // label needs the room.
          flex: stacked ? "none" : "0 1 50%",
          minWidth: 0,
          display: "flex",
          justifyContent: stacked ? "stretch" : "flex-end",
        }}
      >
        {children}
      </Box>
    </Flx>
  );

  if (!hiddenRule) return row;
  // Box wrapper: Flx is a plain function component (no forwardRef), and the
  // Tooltip needs a ref-able child to anchor to.
  return (
    <Tooltip
      arrow
      placement="top"
      title={<HiddenRuleTip clauses={hiddenRule} />}
    >
      <Box>{row}</Box>
    </Tooltip>
  );
};

/* ── ƒx chip ── */
const FxChip = () => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      height: 20,
      px: "6px",
      flexShrink: 0,
      borderRadius: "4px",
      background: T.chipBg,
      color: T.chipText,
      border: `1px solid ${T.chipRing}`,
      fontFamily: MONO,
      fontSize: 11,
      fontWeight: 600,
    }}
  >
    ƒx
  </Box>
);

/* ── calculated value: ƒx chip + value, formula on hover ── */
const CalcValue = ({ value, formula, emphasis }) => {
  const has = value != null && value !== "";
  return (
    <Tooltip
      arrow
      placement="left"
      title={
        <Box sx={{ maxWidth: 260 }}>
          <Flx ac g={0.75} sx={{ mb: 0.75, fontWeight: 600, fontSize: 12 }}>
            <SigmaIcon sx={{ fontSize: 14, color: "#67e8f9" }} />{" "}
            Auto-calculated
          </Flx>
          <Box
            sx={{
              fontFamily: MONO,
              fontSize: 11.5,
              lineHeight: 1.5,
              whiteSpace: "pre-line",
              background: "rgba(255,255,255,0.08)",
              borderRadius: "4px",
              px: 1,
              py: 0.75,
            }}
          >
            {formula || "Auto-calculated value."}
          </Box>
        </Box>
      }
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          cursor: "help",
          px: 0.5,
        }}
      >
        <FxChip />
        <Txt
          sx={{
            fontSize: 13,
            fontWeight: emphasis ? 700 : 600,
            color: has ? T.value : T.muted,
            fontStyle: has ? "normal" : "italic",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {has ? value : "Not set"}
        </Txt>
      </Box>
    </Tooltip>
  );
};

/* ── system (read-only) value: lock + muted text ── */
// A system value that is an http(s) URL (e.g. the Box folder links) renders as
// a clickable link that opens in a new tab, rather than dead text.
const isUrlValue = (v) =>
  typeof v === "string" && /^https?:\/\/\S+$/i.test(v.trim());

const SystemValue = ({ value, tip }) => {
  const has = value != null && value !== "";
  const url = isUrlValue(value) ? value.trim() : null;
  return (
    <Tooltip
      arrow
      placement="left"
      title={
        <Box sx={{ fontSize: 11.5, lineHeight: 1.5, wordBreak: "break-all" }}>
          {url
            ? `${url} — opens in a new tab`
            : tip || "System field — set automatically, can’t be edited."}
        </Box>
      }
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          cursor: url ? "pointer" : "default",
          px: 0.5,
          minWidth: 0,
        }}
      >
        <LockOutlined sx={{ fontSize: 14, color: T.muted, flexShrink: 0 }} />
        {url ? (
          <Box
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            // Don't let the row's click handlers swallow the navigation.
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
              maxWidth: 230,
              fontSize: 13,
              fontWeight: 500,
              color: T.chipText,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {url}
            </Box>
            <OpenInNewRounded sx={{ fontSize: 13, flexShrink: 0 }} />
          </Box>
        ) : (
          <Txt
            sx={{
              fontSize: 13,
              color: T.label,
              fontStyle: has ? "normal" : "italic",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {has ? value : "Not set"}
          </Txt>
        )}
      </Box>
    </Tooltip>
  );
};

/* ── boolean: Yes/No switch (wired straight to the form field) ── */
const YesNoSwitch = ({ name }) => (
  <Field name={name} subscription={{ value: true }}>
    {({ input }) => {
      const on = !!input.value;
      return (
        <Flx
          ac
          g={1.25}
          onClick={() => input.onChange(!on)}
          sx={{ cursor: "pointer", userSelect: "none", px: 0.5, py: 0.5 }}
        >
          <Txt
            sx={{
              fontSize: 13,
              width: 26,
              textAlign: "right",
              fontWeight: on ? 600 : 400,
              color: on ? T.value : T.muted,
            }}
          >
            {on ? "Yes" : "No"}
          </Txt>
          <Box
            sx={{
              position: "relative",
              width: 36,
              height: 20,
              borderRadius: 999,
              flexShrink: 0,
              transition: "background .15s",
              background: on ? T.focus : "#e2e8f0",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 2,
                left: on ? 18 : 2,
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: "#fff",
                boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                transition: "left .15s",
              }}
            />
          </Box>
        </Flx>
      );
    }}
  </Field>
);

/* ── read-only boolean (SYS-set flags like finalBudgetUploaded) ── */
const SystemBool = ({ value, tip }) => (
  <SystemValue value={isNil(value) ? null : value ? "Yes" : "No"} tip={tip} />
);

/* ── editable input: re-skins the existing Rff field into the mockup box ── */
const NUMERIC_TYPES = ["dollar", "percent", "number", "integer", "float"];

// Opens the field's current value in a new tab — enabled only when it's a URL.
const OpenLinkButton = ({ name }) => (
  <Field name={name} subscription={{ value: true }}>
    {({ input }) => {
      const url = isUrlValue(input.value) ? String(input.value).trim() : null;
      return (
        <Tooltip
          arrow
          title={url ? "Open link in a new tab" : "Enter a valid URL to open"}
        >
          <span style={{ display: "inline-flex" }}>
            <IconButton
              size="small"
              disabled={!url}
              onClick={() =>
                url && window.open(url, "_blank", "noopener,noreferrer")
              }
              sx={{ color: url ? T.chipText : T.muted, p: "6px" }}
            >
              <OpenInNewRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      );
    }}
  </Field>
);

const EditControl = ({ field }) => {
  const t = field.type;
  const numeric = NUMERIC_TYPES.includes(t);

  const controlSx = {
    "& .MuiFormControl-root": { width: "100%", m: 0 },
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      background: "#fff",
      minHeight: 36,
      fontSize: "13px",
      fontWeight: 500,
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input, & .MuiSelect-select": {
      minHeight: "20px",
      height: "auto",
      py: "8px",
      px: "10px",
      fontSize: "13px",
      lineHeight: 1.4,
      textAlign: numeric ? "right" : "left",
    },
    "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
      padding: 0,
      alignItems: "flex-start",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: T.border },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: T.borderHover,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: T.focus,
      borderWidth: "1px",
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: `0 0 0 3px ${T.focusRing}`,
    },
    "& .MuiInputAdornment-root": { m: 0 },
  };

  // Linkable fields (e.g. the Box folder URLs) stay editable but gain an
  // open-in-new-tab button beside the input.
  if (field.linkable) {
    return (
      <Flx ac g={0.5} sx={{ width: "100%", maxWidth: "100%" }}>
        <Box sx={{ ...controlSx, flex: 1, minWidth: 0 }}>
          <RffFieldComponentRenderer field={field} />
        </Box>
        <OpenLinkButton name={field.name} />
      </Flx>
    );
  }

  return (
    <Box sx={{ ...controlSx, width: "100%", maxWidth: "100%" }}>
      <RffFieldComponentRenderer field={field} />
    </Box>
  );
};

// Single-select field types where an out-of-list current value should be kept
// visible (see withCurrentValueOption).
const SELECT_TYPES = new Set(["select", "selectAutocomplete"]);

const optionValueOf = (o) => (o && typeof o === "object" ? o.value : o);

// If the field's current value isn't among its options, append it so it stays
// visible/selectable instead of rendering as an empty "Select…" placeholder —
// e.g. a legacy exitStrategy, or a value the product's option set doesn't list.
// Matches the option shape (plain strings vs { value, label }).
const withCurrentValueOption = (options, raw) => {
  const opts = Array.isArray(options) ? options : [];
  if (raw == null || raw === "") return opts;
  if (opts.some((o) => optionValueOf(o) === raw)) return opts;
  const asObjects = opts.length > 0 && typeof opts[0] === "object";
  return [...opts, asObjects ? { value: raw, label: String(raw) } : raw];
};

/* ── choose the control for a field's role ──
   NOTE: values are read with lodash get() so NESTED paths
   (borrowers[0].verifiedLiquidity) resolve for calc/system rows. */
const renderControl = (f, loan) => {
  const raw = get(loan, f.name);
  // A row whose value is edited somewhere else (a modal) renders its own control.
  if (f.render) return f.render(loan);
  if (f.calc)
    return (
      <CalcValue
        value={fmtCalc(f.type, raw)}
        formula={f.formula}
        emphasis={f.emphasis}
      />
    );
  if (f.disabled) {
    if (f.type === "booleanCheckbox" || f.type === "booleanToggle")
      return <SystemBool value={raw} tip={f.tip} />;
    const value = <SystemValue value={fmtCalc(f.type, raw)} tip={f.tip} />;
    // `before`: an action rendered left of the lock (e.g. Override classification).
    return f.before ? (
      <Flx ac g={1}>
        {f.before}
        {value}
      </Flx>
    ) : (
      value
    );
  }
  if (f.type === "booleanCheckbox" || f.type === "booleanToggle")
    return <YesNoSwitch name={f.name} />;

  // Free-text values can be long (e.g. Resolved Credit Score Basis) — render
  // editable text as an AUTO-GROWING textarea so it wraps instead of
  // overflowing. minRows=1 keeps short fields looking like a single-line input;
  // it only grows once the text wraps. Linkable URL fields keep their
  // single-line + open-link layout.
  const isText = f.type === "text" || f.type === "string";
  const renderType = isText && !f.linkable ? "textarea" : f.type;
  // Keep an out-of-list current value visible in single-selects.
  const options = SELECT_TYPES.has(f.type)
    ? withCurrentValueOption(f.options, raw)
    : f.options;

  return (
    <EditControl
      field={{
        name: f.name,
        type: renderType,
        options,
        placeholder: f.placeholder,
        linkable: f.linkable,
      }}
    />
  );
};

/* small blue count badge (mirrors the nav's dirty badge) */
const DirtyBadge = ({ n }) => (
  <Box
    sx={{
      minWidth: 18,
      height: 18,
      px: "5px",
      borderRadius: "999px",
      background: "#2563EB",
      color: "#fff",
      fontSize: 11,
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {n}
  </Box>
);

/* ───────────────────────── collapsible card ───────────────────────── */
const CollapsibleCard = ({
  id,
  title,
  subtitle,
  icon,
  dirtyCount = 0,
  defaultExpanded = true,
  forceExpanded,
  bodySx,
  children,
}) => {
  const [open, setOpen] = useState(defaultExpanded);
  // An external signal (today: "a search is active") drives the card open or
  // closed. Only fires when the signal itself FLIPS, so a manual toggle in
  // between still stands until the next flip.
  useEffect(() => {
    if (forceExpanded !== undefined) setOpen(forceExpanded);
  }, [forceExpanded]);
  // PERF: don't mount a collapsed card's body (its fields) until it's first
  // expanded — keeps the initial tab render cheap. Once expanded, stay mounted
  // (even if re-collapsed) so react-final-form dirty state survives.
  const everOpenRef = useRef(defaultExpanded);
  if (open) everOpenRef.current = true;
  const mountBody = everOpenRef.current;
  return (
    <Box id={id} sx={{ scrollMarginTop: "80px" }}>
      <Card variant="outlined" sx={{ overflow: "hidden", p: 0 }}>
        <Flx
          ac
          jb
          onClick={() => setOpen((o) => !o)}
          sx={{
            pl: "16px",
            pr: "16px",
            pt: "16px",
            pb: "12px",
            cursor: "pointer",
            borderBottom: open ? "1px solid #eef1f4" : "none",
            "&:hover": { background: "#fafbfc" },
          }}
        >
          <Flx
            ac
            g={1}
            sx={{
              minWidth: 0,
              "& .MuiSvgIcon-root": { fontSize: 18, color: "#0891b2" },
            }}
          >
            {icon}
            <Txt
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#0f172a",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {title}
            </Txt>
            {subtitle && (
              <Txt
                sx={{
                  fontSize: 12,
                  color: T.muted,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {subtitle}
              </Txt>
            )}
          </Flx>
          <Flx ac g={1} sx={{ flexShrink: 0 }}>
            {dirtyCount > 0 && <DirtyBadge n={dirtyCount} />}
            <ExpandMoreOutlined
              sx={{
                fontSize: 20,
                color: "#94a3b8",
                transition: "transform .15s",
                transform: open ? "rotate(180deg)" : "none",
              }}
            />
          </Flx>
        </Flx>
        <Collapse in={open}>
          {mountBody ? (
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5, ...bodySx }}>
              {typeof children === "function" ? children() : children}
            </Box>
          ) : null}
        </Collapse>
      </Card>
    </Box>
  );
};

/* filter a section's fields by the search query.
   Uses get() so nested calc paths hide correctly on nil. */
const filterRows = (fields, q, loan) =>
  fields.filter((f) => {
    if (f.calc && f.hideWhenNil && isNil(get(loan, f.name))) return false;
    if (f.hideWhenEmpty && [null, undefined, ""].includes(get(loan, f.name)))
      return false;
    if (!q) return true;
    return (
      f.name?.toLowerCase().includes(q) || f.label?.toLowerCase().includes(q)
    );
  });

/* pricing-drift warning for a row (§6.1): only on locked loans, only for
   fields on the watch list. Nested names never match the list — loan-level
   keys only, which is exactly the PRICING_IMPACT_FIELDS contents. */
const lockWarnFor = (f, loan) =>
  !f.calc &&
  !f.disabled &&
  loan?.loanLocked &&
  PRICING_IMPACT_FIELDS.has(f.name)
    ? LOCK_DRIFT_HINT
    : null;

/* Search terms the experience category editor answers to — its inputs aren't
   in section.fields, so filterRows can't see them. */
const EXPERIENCE_EDITOR_TERMS =
  "self reported experience fix flip sold retained rental income producing properties ground up construction general contractor rehab counts";

/* Dirty inputs inside the experience editor, for the section/entity badges —
   same reason: its paths never appear in section.fields. */
const countDirtyExperience = (experience, dirtyFields) =>
  experience
    ? experienceFieldPaths(experience.prefix, experience.categoryFlags).filter(
        (path) => dirtyFields?.[path],
      ).length
    : 0;

/* ───────────────────────── one loan-info section card ───────────────────────── */
const SectionCard = ({ section, loan, quickFilter, dirtyFields }) => {
  const q = quickFilter?.trim().toLowerCase();
  const rows = useMemo(
    () => filterRows(section.fields, q, loan),
    [section.fields, loan, q],
  );

  // Interest reserves is a special read-only roll-up (per-property + total),
  // shown on every product except DSCR.
  const isReserves = section.id === "reserves";
  const showReserveRollup = isInterestReserve(loan?.loanProductType);
  const reserveProps = showReserveRollup ? loan?.subjectProperties || [] : [];

  if (!isReserves && rows.length === 0) return null;

  const dirtyCount = section.fields.filter(
    (f) => !f.calc && dirtyFields?.[f.name],
  ).length;

  return (
    <CollapsibleCard
      id={`linfo-sec-${section.id}`}
      title={section.label}
      icon={<Box component="span">{section.icon}</Box>}
      dirtyCount={dirtyCount}
    >
      {isReserves ? (
        <>
          {rows.map((f) => (
            <FieldRow
              key={f.name}
              label={f.label}
              required={f.required}
              hint={f.hint}
              warnHint={lockWarnFor(f, loan)}
              hiddenRule={f.hiddenRule}
              stacked={f.stacked}
            >
              {renderControl(f, loan)}
            </FieldRow>
          ))}
          {reserveProps.map((p, i) => (
            <FieldRow
              key={p._id || i}
              label={addressLabel(p?.address) || p?._id || `Property ${i + 1}`}
            >
              <SystemValue
                value={fmtCalc("dollar", p?.interestReserves || 0)}
                tip="Per-property interest reserve. Edit on the property card below."
              />
            </FieldRow>
          ))}
          {showReserveRollup && (
            <>
              <FieldRow label="Total Interest Reserves">
                <CalcValue
                  value={fmtCalc("dollar", loan?.totalInterestReserves)}
                  emphasis
                  formula="Σ interestReserves across all subject properties."
                />
              </FieldRow>
              <FieldRow label="Capitalized (Financed) Interest Reserve">
                <CalcValue
                  value={fmtCalc("dollar", loan?.capitalizedInterestReserve)}
                  formula="Σ interestReserves on properties flagged 'Interest Reserve Financed'. The financed portion, capitalized into UPB (borrower-cash reserves excluded)."
                />
              </FieldRow>
            </>
          )}
        </>
      ) : (
        rows.map((f) => (
          <FieldRow
            key={f.name}
            field={f.name}
            label={f.label}
            required={f.required}
            hint={f.hint}
            warnHint={lockWarnFor(f, loan)}
            hiddenRule={f.hiddenRule}
            stacked={f.stacked}
          >
            {renderControl(f, loan)}
          </FieldRow>
        ))
      )}
    </CollapsibleCard>
  );
};

/* ───────────────── one borrower / property entity card ───────────────── */
/* ── documents card shown at the bottom of a borrower/property block ──
   Shows the entity's associated loan documents in the SAME table used by the
   "All Borrower Related Documents" area (LoanDocumentManagerTable grouped by
   docGroup) plus an Upload button that opens the reusable document uploader
   locked to this entity (docUploadModalOpen). */
const EntityDocumentsCard = ({ entityType, entityId, entityLabel }) => {
  const dispatch = useDispatch();
  const loan = useSelector((s) => s.loanDrilldown);
  const {
    getAssociationDocumentsByBorrowerId,
    getAssociationDocumentsByPropertyId,
  } = useAssociationUtils();

  const docs = useMemo(() => {
    if (entityType === "borrower")
      return getAssociationDocumentsByBorrowerId(loan, entityId) || [];
    if (entityType === "property")
      return getAssociationDocumentsByPropertyId(loan, entityId) || [];
    return [];
  }, [
    entityType,
    entityId,
    loan,
    getAssociationDocumentsByBorrowerId,
    getAssociationDocumentsByPropertyId,
  ]);

  const [open, setOpen] = useState(false);

  const handleUpload = (e) => {
    // The header row toggles the card — don't collapse it on the way to the
    // upload modal.
    e.stopPropagation();
    dispatch(
      docUploadModalOpen({
        type: entityType,
        id: entityId,
        label: entityLabel,
      }),
    );
  };

  return (
    <Box
      sx={{
        mt: 2.5,
        background: "#ffffff",
        border: "1px solid #E4E7EC",
        borderRadius: "14px",
        // No padding on the card itself — the documents table renders full-bleed
        // (matching the "All Borrower Related Documents" area); the header keeps
        // the same 18/22 padding it had. overflow:hidden clips the table to the
        // rounded corners.
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
      }}
    >
      <Flx
        ac
        jb
        g={1}
        onClick={() => setOpen((o) => !o)}
        sx={{
          px: "22px",
          pt: "18px",
          pb: "18px",
          cursor: "pointer",
          // Matches the divider the table draws above its own search bar, so
          // the header reads as a header rather than as another table row.
          borderBottom: open ? "1px solid #eef1f4" : "none",
          "&:hover": { background: "#fafbfc" },
        }}
      >
        <Flx ac g={1} sx={{ minWidth: 0 }}>
          <DescriptionOutlined sx={{ fontSize: 18, color: "#0891b2" }} />
          <Txt
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#0891b2",
            }}
          >
            Documents
          </Txt>
          <Txt sx={{ fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>
            {docs.length}
          </Txt>
        </Flx>
        <Flx ac g={1} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={
              <UploadFileOutlined sx={{ fontSize: "16px !important" }} />
            }
            onClick={handleUpload}
            sx={{
              textTransform: "none",
              fontSize: 12,
              py: 0.375,
              px: 1.25,
              minHeight: 0,
            }}
          >
            Upload
          </Button>
          <ExpandMoreOutlined
            sx={{
              fontSize: 20,
              color: "#94a3b8",
              transition: "transform .15s",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        </Flx>
      </Flx>
      {/* unmountOnExit: the card starts collapsed, so the documents grid never
          mounts until someone asks for it. Nothing in it holds state worth
          preserving across a re-collapse. */}
      <Collapse in={open} unmountOnExit>
        {docs.length === 0 ? (
          <Txt
            sx={{ fontSize: 12.5, color: "#94a3b8", px: "22px", py: "18px" }}
          >
            No documents linked to this{" "}
            {entityType === "property" ? "property" : "borrower"} yet.
          </Txt>
        ) : (
          <LoanDocumentManagerTable
            rowData={docs}
            forcedLayout="docGroup"
            minHeight={"unset"}
          />
        )}
      </Collapse>
    </Box>
  );
};

const EntityCard = ({
  id,
  title,
  subtitle,
  icon,
  sections,
  loan,
  quickFilter,
  dirtyFields,
  footerContent,
}) => {
  const q = quickFilter?.trim().toLowerCase();

  const visibleSections = useMemo(
    () =>
      sections
        .map((s) => ({
          ...s,
          rows: filterRows(s.fields, q, loan),
          // The experience editor's inputs aren't in s.fields, so filterRows
          // can't see them — match the search against its own terms instead.
          showExperience:
            !!s.experience && (!q || EXPERIENCE_EDITOR_TERMS.includes(q)),
        }))
        .filter((s) => s.rows.length > 0 || s.showExperience),
    [sections, q, loan],
  );

  const dirtyCount = useMemo(
    () =>
      sections.reduce(
        (acc, s) =>
          acc +
          s.fields.filter((f) => dirtyFields?.[f.name]).length +
          countDirtyExperience(s.experience, dirtyFields),
        0,
      ),
    [sections, dirtyFields],
  );

  if (visibleSections.length === 0) return null;

  return (
    <CollapsibleCard
      id={id}
      title={title}
      subtitle={subtitle}
      icon={icon}
      dirtyCount={dirtyCount}
      // Borrower / property cards start COLLAPSED — the loan-level cards are
      // the tab's subject, and an entity block is tall. A search opens the
      // matching ones (and closes them again when it is cleared).
      defaultExpanded={!!q}
      forceExpanded={!!q}
      bodySx={{ background: "#f8fafc", px: 2.5, pt: 2.5, pb: 2.5 }}
    >
      {/* render-prop: the (many) entity field elements aren't built until the
          card is first expanded — keeps the initial tab render fast. */}
      {() => (
        <>
          <Box
            sx={{
              columnWidth: "540px",
              columnGap: "20px",
              "& > *": {
                breakInside: "avoid",
                WebkitColumnBreakInside: "avoid",
                mb: 2.5,
                backgroundColor: "#FFFFFF",
                border: "1px solid #E4E7EC",
                borderBottom: "1px solid #eef1f4",
                borderRadius: "14px",
                padding: "22px 26px",
                boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
              },
            }}
          >
            {visibleSections.map((s) => (
              <Box key={s.id}>
                <Txt
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "#0891b2",
                    mb: 1,
                  }}
                >
                  {s.label}
                </Txt>
                {s.rows.map((f) => (
                  <FieldRow
                    key={f.name}
                    label={f.label}
                    required={f.required}
                    hint={f.hint}
                    hiddenRule={f.hiddenRule}
                    stacked={f.stacked}
                  >
                    {renderControl(f, loan)}
                  </FieldRow>
                ))}
                {/* Full column width rather than a FieldRow — the category
                    cards need the room, and they're a control cluster, not a
                    single labelled value. */}
                {s.showExperience && (
                  <Box sx={{ pt: s.rows.length ? 1.5 : 0 }}>
                    <Txt
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: "#64748b",
                      }}
                    >
                      Self-Reported
                    </Txt>
                    <BorrowerExperienceEditor
                      prefix={s.experience.prefix}
                      categoryFlags={s.experience.categoryFlags}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
          {/* Documents card — full width below the field columns. Only shown
              when not actively filtering (the search is field-scoped). */}
          {!q && footerContent}
        </>
      )}
    </CollapsibleCard>
  );
};

/* ───── horizontal field key — compact, sits beside the title ───── */
const HorizontalFieldKey = ({ reveal }) => (
  <Flx
    ac
    g={3}
    sx={{
      flexShrink: 0,
      alignSelf: "stretch",
      px: 1.25,
      py: 0.5,
      border: `1px solid ${grey[200]}`,
      borderRadius: "10px",
      background: "#fff",
    }}
  >
    <Flx ac g={0.6}>
      <LockOutlined sx={{ fontSize: 15, color: T.muted }} />
      <Txt sx={{ fontSize: 12, color: "#5A6B7B", whiteSpace: "nowrap" }}>
        Locked
      </Txt>
    </Flx>
    <Flx ac g={0.6}>
      <FxChip />
      <Txt sx={{ fontSize: 12, color: "#5A6B7B", whiteSpace: "nowrap" }}>
        Auto-calculated
      </Txt>
    </Flx>
    <Flx ac g={0.6}>
      <Box
        sx={{ width: 9, height: 9, borderRadius: "50%", background: "#2563EB" }}
      />
      <Txt sx={{ fontSize: 12, color: "#5A6B7B", whiteSpace: "nowrap" }}>
        Unsaved
      </Txt>
    </Flx>
    {reveal && (
      <Flx ac g={0.6}>
        <VisibilityOffOutlined sx={{ fontSize: 15, color: amber.dark }} />
        <Txt sx={{ fontSize: 12, color: "#5A6B7B", whiteSpace: "nowrap" }}>
          Conditionally hidden
        </Txt>
      </Flx>
    )}
  </Flx>
);

/* ───────────────────────── nested "On this page" nav ───────────────────────── */
/* thin rail shown when the nav pane is collapsed — click anything to expand */
const CollapsedNavRail = ({ onExpand }) => (
  <Flx
    column
    ac
    sx={{
      position: "sticky",
      // Offset by the drilldown's pinned tab nav.
      top: "calc(var(--drilldown-nav-h, 0px) + 8px)",
      alignSelf: "start",
      py: 1,
      gap: 1,
    }}
  >
    <Tooltip title="Expand sidebar" arrow placement="right">
      <IconButton
        size="small"
        onClick={onExpand}
        aria-label="Expand sidebar"
        sx={{
          p: 0.3,
          color: "#64748b",
          "&:hover": { background: "#f1f5f9", color: "#0f172a" },
        }}
      >
        <ChevronRightRounded sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
    <Tooltip title="On this page" arrow placement="right">
      <Box
        onClick={onExpand}
        sx={{
          cursor: "pointer",
          color: "#64748b",
          "&:hover": { color: "#0f172a" },
        }}
      >
        <ListAltOutlined sx={{ fontSize: 18 }} />
      </Box>
    </Tooltip>
    <Box
      onClick={onExpand}
      sx={{
        cursor: "pointer",
        mt: 1,
        writingMode: "vertical-rl",
        transform: "rotate(180deg)",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: "#64748b",
        userSelect: "none",
        "&:hover": { color: "#0f172a" },
      }}
    >
      On this page
    </Box>
  </Flx>
);

const NestedNav = ({
  groups,
  activeId,
  onSelect,
  onSelectGroup,
  quickFilter,
  setQuickFilter,
  onCollapse,
}) => (
  <Box
    sx={{
      position: "sticky",
      // Offset by the drilldown's pinned tab nav — and shorten the rail by the
      // same amount so it still fits between the nav and the viewport bottom.
      top: "calc(var(--drilldown-nav-h, 0px) + 8px)",
      alignSelf: "start",
      maxHeight: "calc(100vh - 100px - var(--drilldown-nav-h, 0px))",
      overflowY: "auto",
      px: 1.5,
      py: 2,
    }}
  >
    {/* header row: label + collapse button (like the Entity tab) */}
    <Flx ac jb sx={{ mb: 1, pl: 0.5 }}>
      <Txt
        sx={{
          fontSize: 11,
          fontWeight: 400,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "#94a3b8",
        }}
      >
        On this page
      </Txt>
      {onCollapse && (
        <Tooltip title="Collapse" arrow>
          <IconButton
            size="small"
            onClick={onCollapse}
            aria-label="Collapse sidebar"
            sx={{
              p: 0.3,
              color: "#64748b",
              "&:hover": { background: "#f1f5f9", color: "#0f172a" },
            }}
          >
            <ChevronLeftRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
    </Flx>
    <Box sx={{ mb: 2.5 }}>
      <SearchInput
        value={quickFilter}
        onChange={setQuickFilter}
        variant="paper"
        placeholder="Find a field…"
        sx={{
          width: "100%",
          "& .MuiInputBase-root": { borderRadius: "10px", fontSize: "13px" },
        }}
      />
    </Box>
    <Flx column g={3}>
      {groups.map((grp) => (
        <Box key={grp.id}>
          {grp.label && (
            <Flx
              ac
              g={1}
              onClick={() => onSelectGroup(grp)}
              sx={{
                px: 1.25,
                py: 0.6,
                borderRadius: "8px",
                cursor: "pointer",
                "& .MuiSvgIcon-root": { fontSize: 16, color: "#64748b" },
                "&:hover": { background: "#EDF1F5" },
              }}
            >
              <Txt
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "#334155",
                }}
              >
                {grp.label}
              </Txt>
              {grp.dirtyCount > 0 && (
                <Box sx={{ ml: "auto" }}>
                  <DirtyBadge n={grp.dirtyCount} />
                </Box>
              )}
            </Flx>
          )}
          <Flx column g={0.15} sx={{ pl: grp.label ? 1 : 0, mt: 0.25 }}>
            {grp.items.map((it) => {
              const active = it.id === activeId;
              return (
                <Flx
                  key={it.id}
                  ac
                  g={1}
                  onClick={() => onSelect(it.id)}
                  sx={{
                    pr: 1,
                    py: 0.55,
                    cursor: "pointer",
                    borderRight: `2px solid ${active ? "#0E8794" : "transparent"}`,
                    background: active ? "#E6F5F7" : "transparent",
                    color: active ? "#0A6570" : "#475569",
                    "& .MuiSvgIcon-root": {
                      fontSize: 15,
                      flexShrink: 0,
                      color: active ? "#0A6570" : "#94A3B8",
                    },
                    "&:hover": { background: active ? "#E6F5F7" : "#EDF1F5" },
                  }}
                >
                  {it.icon}
                  <Txt
                    sx={{
                      fontSize: 13,
                      fontWeight: active ? 600 : 400,
                      color: "inherit",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {it.label}
                  </Txt>
                  {it.dirtyCount > 0 && (
                    <Box sx={{ ml: "auto" }}>
                      <DirtyBadge n={it.dirtyCount} />
                    </Box>
                  )}
                </Flx>
              );
            })}
          </Flx>
        </Box>
      ))}
    </Flx>
  </Box>
);

/* group heading in the content column */
const GroupHeading = ({ id, icon, label, count }) => (
  <Flx ac g={1} id={id} sx={{ mt: 0.5, mb: 1, scrollMarginTop: "80px" }}>
    {/* {icon} */}
    <Txt sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{label}</Txt>
    {count != null && (
      <Txt sx={{ fontSize: 12.5, color: T.muted, fontWeight: 500 }}>
        ({count})
      </Txt>
    )}
  </Flx>
);

/* zone heading — "Deal terms · what is this loan?" */
const ZoneHeading = ({ id, label, hint }) => (
  <Flx ac g={1.25} id={id} sx={{ mt: 0.5, mb: 1, scrollMarginTop: "90px" }}>
    <Txt sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{label}</Txt>
    {hint && (
      <Txt sx={{ fontSize: 12.5, color: T.muted, fontStyle: "italic" }}>
        {hint}
      </Txt>
    )}
  </Flx>
);

/* ═══════════════ SummaryStrip ═══════════════
   Each row has ONE job, nothing repeats:
     • Top metrics  → deal size, price, term/DSCR, time-to-close.
     • Right rail   → identity + state: lock, RTL class, stage, product · #.
     • Leverage bar → the only home for the ratios. */

// Calendar days from today (Eastern) to a date-only field. Reads the stored
// day literally (same rule as formatDate) — `new Date(d).setHours(0)` moved a
// UTC-midnight value ("…T00:00:00Z") to the previous local day.
const daysUntil = daysUntilDateOnly;

/* one headline number with an optional muted sub-caption. Fills its grid
   cell (equal width); the sub-caption wraps when the cell is narrow. */
const Metric = ({ label, value, sub, subColor }) => (
  <Flx column g={0.25} sx={{ minWidth: 0 }}>
    <Txt
      sx={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: T.muted,
      }}
    >
      {label}
    </Txt>
    <Txt sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
      {value || "—"}
    </Txt>
    {sub && (
      <Txt
        component="div"
        sx={{ fontSize: 11.5, color: subColor || T.muted, lineHeight: 1.35 }}
      >
        {sub}
      </Txt>
    )}
  </Flx>
);

/* one leverage ratio (label + ƒx + value) with its formula on hover */
const LeverageEntry = ({ field, loan }) => (
  <Tooltip
    arrow
    placement="top"
    title={
      <Box sx={{ maxWidth: 260 }}>
        <Flx ac g={0.75} sx={{ mb: 0.75, fontWeight: 600, fontSize: 12 }}>
          <SigmaIcon sx={{ fontSize: 14, color: "#67e8f9" }} /> Auto-calculated
        </Flx>
        <Box
          sx={{
            fontFamily: MONO,
            fontSize: 11.5,
            lineHeight: 1.5,
            whiteSpace: "pre-line",
            background: "rgba(255,255,255,0.08)",
            borderRadius: "4px",
            px: 1,
            py: 0.75,
          }}
        >
          {field.formula || "Auto-calculated value."}
        </Box>
      </Box>
    }
  >
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        cursor: "help",
      }}
    >
      <Txt sx={{ fontSize: 13, color: T.label }}>{field.label}</Txt>
      <FxChip />
      <Txt sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
        {fmtCalc(field.type, get(loan, field.name)) ?? "—"}
      </Txt>
    </Box>
  </Tooltip>
);

/* leverage cell for the summary box: bold label + value (no per-row chip —
   the box header carries one "ƒx calculated" chip); formula on hover. */
const LeverageCell = ({ field, loan }) => (
  <Tooltip
    arrow
    placement="top"
    title={
      <Box sx={{ maxWidth: 260 }}>
        <Flx ac g={0.75} sx={{ mb: 0.75, fontWeight: 600, fontSize: 12 }}>
          <SigmaIcon sx={{ fontSize: 14, color: "#67e8f9" }} /> Auto-calculated
        </Flx>
        <Box
          sx={{
            fontFamily: MONO,
            fontSize: 11.5,
            lineHeight: 1.5,
            whiteSpace: "pre-line",
            background: "rgba(255,255,255,0.08)",
            borderRadius: "4px",
            px: 1,
            py: 0.75,
          }}
        >
          {field.formula || "Auto-calculated value."}
        </Box>
      </Box>
    }
  >
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: "12px",
        cursor: "help",
      }}
    >
      <Txt
        sx={{
          // fontSize: 12.5,
          // fontWeight: 600,
          // color: T.label,

          fontWeight: 600,
          fontSize: "11px",
          color: "#94a3b8",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {field.label}
      </Txt>
      <Txt sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>
        {fmtCalc(field.type, get(loan, field.name)) ?? "—"}
      </Txt>
    </Box>
  </Tooltip>
);

const SummaryStrip = ({ loan, leverageSection, isAcq }) => {
  const dscr = isDSCR(loan?.loanProductType);

  // Total loan: show the base + holdback composition inline.
  const hasHoldback = Number(loan?.totalHoldback) > 0;
  const totalSub = hasHoldback
    ? `${fmtCalc("dollar", loan?.baseLoanAmount)} base + ${fmtCalc(
        "dollar",
        loan?.totalHoldback,
      )} holdback`
    : null;

  // Rate: points are the other half of the price.
  const pts =
    (Number(loan?.originationPoints) || 0) +
    (Number(loan?.discountPoints) || 0);
  const rateSub = pts > 0 ? `${fmtCalc("percent", pts)} pts` : "0 pts";

  // Closes. A CLOSED loan has no countdown to run — expectedClosingDate is a
  // stale forecast once the note is signed, so an already-closed loan must never
  // be flagged "overdue". Postclose (acquisitions) that is the normal state: the
  // loan closed at the correspondent before we ever priced the trade, and the
  // metric reports the note date + our settlement instead of a deadline.
  const hasClosed = !!loan?.loanClosed || !!loan?.originationDate;
  const closedOn = loan?.originationDate || loan?.fundedDate;

  let closesLabel = "Closes";
  let closesValue = loan?.expectedClosingDate
    ? formatDate(loan.expectedClosingDate)
    : "—";
  let closesSub = null;
  let closesColor = T.muted;

  if (hasClosed) {
    closesLabel = "Closed";
    closesValue = closedOn ? formatDate(closedOn) : "Closed";
    closesSub = isAcq
      ? loan?.settlementDate
        ? `purchase funded ${formatDate(loan.settlementDate)}`
        : loan?.expectedSettlementDate
          ? `settles ~${formatDate(loan.expectedSettlementDate)}`
          : "awaiting settlement"
      : loan?.fundedDate
        ? `funded ${formatDate(loan.fundedDate)}`
        : null;
  } else if (isAcq) {
    // An acquisition that is not flagged closed was locked BEFORE the seller's
    // close. The date is the CORRESPONDENT's closing, not a deadline we own, and
    // there is no closingCutoffDate on the acquisitions model — so this never
    // escalates to the red "overdue" treatment. Past the date it reads as a
    // data-hygiene nudge: the note has almost certainly been signed and the loan
    // just hasn't been marked closed yet.
    closesLabel = "Expected close";
    const dc = daysUntil(loan?.expectedClosingDate);
    if (dc !== null) {
      if (dc < 0) {
        closesSub = `${Math.abs(dc)}d ago · not marked closed`;
        closesColor = "#d97706";
      } else if (dc === 0) {
        closesSub = "today";
      } else {
        closesSub = `in ${dc} day${dc === 1 ? "" : "s"}`;
      }
    } else {
      closesSub = "not marked closed";
    }
  } else {
    // Preclose and still open — run the countdown and the cutoff flag.
    const dc = daysUntil(loan?.expectedClosingDate);
    const pastCutoff =
      loan?.closingCutoffDate &&
      loan?.expectedClosingDate &&
      dateOnlyKey(loan.expectedClosingDate) > dateOnlyKey(loan.closingCutoffDate);
    if (dc !== null) {
      if (dc < 0) {
        closesSub = `${Math.abs(dc)}d overdue`;
        closesColor = "#dc2626";
      } else if (dc === 0) {
        closesSub = "today";
        closesColor = "#dc2626";
      } else {
        closesSub = `in ${dc} day${dc === 1 ? "" : "s"}`;
        if (dc <= 7) closesColor = "#d97706";
      }
    }
    if (pastCutoff) {
      closesSub = `${closesSub} · past cutoff`;
      closesColor = "#dc2626";
    }
  }

  const metrics = [
    {
      label: "Total loan",
      value: fmtCalc("dollar2", loan?.totalLoanAmount),
      sub: totalSub,
    },
    {
      label: "Rate",
      // Postclose the note rate is the truth; interestRate mirrors it and can be
      // blank on a freshly imported trade tape.
      value: fmtCalc("percent", loan?.interestRate ?? loan?.noteRate),
      sub: rateSub,
    },
    dscr
      ? {
          label: "DSCR",
          value: !isNil(loan?.dscr) ? `${Number(loan.dscr).toFixed(2)}x` : "—",
        }
      : {
          label: "Term",
          value: loan?.termMonths ? `${loan.termMonths} mo` : "—",
          sub: loan?.maturityDate
            ? `matures ${formatDate(loan.maturityDate)}`
            : null,
        },
    {
      label: closesLabel,
      value: closesValue,
      sub: closesSub,
      subColor: closesColor,
    },
  ];

  const leverageRows = (leverageSection?.fields || []).filter(
    (f) => !(f.hideWhenNil && isNil(get(loan, f.name))),
  );

  return (
    <Flx
      column
      id="summary"
      g={2}
      sx={{
        scrollMarginTop: "90px",
        px: 2.5,
        py: 2,
        mb: 2,
        borderRadius: "12px",
        border: `1px solid ${grey[200]}`,
        background: "#fff",
        // container query context: the leverage box wraps based on THIS card's
        // width (the content area), not the viewport (MUI breakpoints fire too
        // early when the nav pane + chrome narrow the content).
        containerType: "inline-size",
      }}
    >
      <Flx ac wrap jb g={3} sx={{ alignItems: "center" }}>
        {/* metrics — 4 equal-width cells; on narrow screens they shrink and
            the sub-caption wraps, on wide screens they grow and it doesn't. */}
        <Box
          sx={{
            flex: "1 1 460px",
            minWidth: 0,
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 2.5,
            maxWidth: "900px",
          }}
        >
          {metrics.map((m) => (
            <Metric key={m.label} {...m} />
          ))}
        </Box>

        {/* Leverage box — header ("ƒx calculated" chip) + 2-column ratios. */}
        {leverageRows.length > 0 && (
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.25,
              borderRadius: "10px",
              border: `1px solid ${grey[200]}`,
              background: "#fff",
            }}
          >
            <Flx ac jb g={2.5} sx={{ mb: 1 }}>
              <Flx ac g={0.75}>
                <ShowChartOutlined sx={{ fontSize: 16, color: "#0891b2" }} />
                <Txt
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    // color: T.muted,
                    color: "#94a3b8",
                  }}
                >
                  Leverage
                </Txt>
              </Flx>
              <Flx
                ac
                g={0.4}
                sx={{
                  px: 0.75,
                  py: 0.15,
                  borderRadius: "6px",
                  background: T.chipBg,
                  color: T.chipText,
                }}
              >
                <Txt sx={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 700 }}>
                  ƒx
                </Txt>
                <Txt sx={{ fontSize: 10.5, fontWeight: 600 }}>calculated</Txt>
              </Flx>
            </Flx>
            <Box
              sx={{
                display: "grid",
                // 2×2 by default; single row only when the summary card (content
                // area) is wide enough — container query, not viewport.
                gridTemplateColumns: "auto auto",
                "@container (min-width: 1360px)": {
                  gridTemplateColumns: "repeat(4, auto)",
                },
                columnGap: 3.5,
                rowGap: 0.75,
              }}
            >
              {leverageRows.map((f) => (
                <LeverageCell key={f.name} field={f} loan={loan} />
              ))}
            </Box>
          </Box>
        )}
      </Flx>

      {/* Stage progress bar (shared with the Summary tab's StageTracker). */}
      <Box sx={{ pt: 2 }}>
        <StageTracker loan={loan} />
      </Box>
    </Flx>
  );
};

/* leverage — computed readout strip (dashed) for search results */
const LeverageStrip = ({ section, loan }) => {
  const rows = section.fields.filter(
    (f) => !(f.hideWhenNil && isNil(get(loan, f.name))),
  );
  if (!rows.length) return null;
  return (
    <Flx
      ac
      wrap
      id={`linfo-sec-${section.id}`}
      g={3.5}
      sx={{
        scrollMarginTop: "90px",
        mt: 1,
        px: 2.5,
        py: 1.5,
        borderRadius: "12px",
        border: "1px dashed #cbd5e1",
        background: "rgba(241,245,249,0.6)",
      }}
    >
      <Flx ac g={1}>
        <ShowChartOutlined sx={{ fontSize: 17, color: "#0891b2" }} />
        <Txt sx={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>
          Leverage
        </Txt>
      </Flx>
      {rows.map((f) => (
        <LeverageEntry key={f.name} field={f} loan={loan} />
      ))}
    </Flx>
  );
};

/* ══════════════════ unified routed form ══════════════════ */
const LOAN_UNSETTABLE = new Set([
  "salesperson",
  "processor",
  "preUnderwriter",
  "qc_analyst",
  "underwriter",
  "loanCloser",
  "referralPartner",
  "referralSource",
  "investorCounterparty",
  "fundingEntity",
  "subStatus",
  "exitStrategy",
  "sync_loan_id",
  "interestCalcType",
  "originator",
]);

// Fields that should be CLEARED via `$set null` (not `$unset`) when emptied.
// Clearing an RFF text field yields `undefined`, which JSON drops from the
// update_loan_data payload (so the old value silently persists); and the
// backend's unset allowlist rejects these, so `$unset` is out too. Sending
// explicit `null` makes update_loan_data $set-clear them. NOTE: the Box folder
// URLs are Salesforce-authoritative (Link_to_Box_*), so an inbound SF pull can
// repopulate them after a clear.
const LOAN_NULL_ON_CLEAR = new Set([
  "box_loan_folder_url",
  "box_correspondent_folder_url",
]);

const runHook = (fn, args) =>
  new Promise((resolve, reject) => {
    fn({
      ...args,
      onSuccessFn: (res) => resolve(res),
      onFailFn: (e) => reject(e || new Error("Save failed")),
    });
  });

/**
 * The floating save affordance for the whole tab — the same animated pill the
 * correspondent profile and the Wire & Funding wire ticket use.
 *
 * It renders unconditionally (the pill owns its own enter/exit and the green
 * "All changes saved" beat, so gating it on `dirty` would cut both off).
 * `submitting` — not the hook's `loading` — drives the saving state: `onSubmit`
 * here fans out across several sequential requests (loan, then each dirty
 * borrower, then each dirty property) and stays pending for all of them.
 */
const UnifiedSaveBar = () => {
  const form = useForm();
  const loan = useSelector((s) => s.loanDrilldown);
  const { dirty, dirtyFields, submitting } = useFormState({
    subscription: { dirty: true, dirtyFields: true, submitting: true },
  });
  const n = Object.keys(dirtyFields || {}).length;
  return (
    <PendingChangesBar
      dirty={dirty}
      saving={submitting}
      count={n}
      label="Save all"
      subText="Loan, borrower and property edits save to their own records."
      onSave={() => form.submit()}
      onDiscard={() => form.restart(normalizeLegacyDelayedPurchase(loan))}
    />
  );
};

const UnifiedEditForm = ({ children }) => {
  const loan = useSelector((s) => s.loanDrilldown);
  const dispatch = useDispatch();
  const {
    updateLoanData,
    updateBorrower,
    updateSubjectProperty,
    unsetLoanDataFields,
  } = useUnderwritingHook();

  const onSubmit = async (values, form) => {
    const loanId = loan?._id;
    if (!loanId) return;
    const dirty = form.getState().dirtyFields || {};

    const loanData = {};
    const loanUnset = [];
    const borrowerMap = {};
    const propMap = {};

    Object.keys(dirty).forEach((path) => {
      const val = get(values, path);
      const bm = path.match(/^borrowers\[(\d+)\]\.(.+)$/);
      const pm = path.match(/^subjectProperties\[(\d+)\]\.(.+)$/);
      if (bm) {
        (borrowerMap[bm[1]] = borrowerMap[bm[1]] || {})[bm[2]] = val;
      } else if (pm) {
        (propMap[pm[1]] = propMap[pm[1]] || {})[pm[2]] = val;
      } else if (LOAN_UNSETTABLE.has(path) && isNil(val)) {
        loanUnset.push(path);
      } else if (LOAN_NULL_ON_CLEAR.has(path) && isNil(val)) {
        // Clear via explicit null so the empty value is sent + $set-cleared
        // (undefined would be dropped from the JSON payload).
        loanData[path] = null;
      } else {
        loanData[path] = val;
      }
    });

    // Purpose + delayed-purchase flag travel TOGETHER (REQ-106). The form is
    // seeded with a legacy "Delayed Purchase" loan already in the new shape
    // (cash-out + Yes), so touching either one must write both — otherwise
    // flipping only the flag would leave the stored legacy purpose in place
    // (and the backend would clear the flag against it). Off cash-out the flag
    // goes up as null, which is what the backend stores there anyway.
    if ("loanPurpose" in loanData || DELAYED_PURCHASE_FIELD in loanData) {
      const purposeVal = get(values, "loanPurpose");
      if (!isNil(purposeVal)) loanData.loanPurpose = purposeVal;
      loanData[DELAYED_PURCHASE_FIELD] = canBeDelayedPurchase(purposeVal)
        ? get(values, DELAYED_PURCHASE_FIELD) ?? null
        : null;
    }

    let last = null;
    try {
      if (Object.keys(loanData).length)
        last = await runHook(updateLoanData, { loanId, data: loanData });
      if (loanUnset.length)
        last = await runHook(unsetLoanDataFields, {
          loanId,
          data: { fields: loanUnset },
        });
      for (const [idx, data] of Object.entries(borrowerMap)) {
        const borrowerId = get(values, `borrowers[${idx}]._id`);
        if (borrowerId)
          last = await runHook(updateBorrower, { loanId, borrowerId, data });
      }
      for (const [idx, data] of Object.entries(propMap)) {
        const propertyId = get(values, `subjectProperties[${idx}]._id`);
        if (propertyId)
          last = await runHook(updateSubjectProperty, {
            loanId,
            propertyId,
            data,
          });
      }
    } catch (e) {
      // the hook already surfaced an error snackbar — still sync whatever saved.
    }
    if (last) {
      dispatch(loanDrilldownSet(last));
      dispatch(loanPipelineClear());
    }
  };

  // A legacy "Delayed Purchase" loan is presented in the new shape (cash-out +
  // Delayed purchase? Yes). Pristine seed values are never saved — only dirty
  // fields are — so opening the tab writes nothing; a save that touches the
  // purpose or the flag writes the new shape (see onSubmit).
  const initialValues = useMemo(
    () => normalizeLegacyDelayedPurchase(loan),
    [loan],
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      mutators={{ ...arrayMutators }}
      subscription={{ submitting: true }}
      render={({ handleSubmit }) => (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {children}
          <UnifiedSaveBar />
        </Box>
      )}
    />
  );
};

/* ───────────────────────── body (inside the form) ───────────────────────── */
const countDirty = (fields, dirtyFields) =>
  fields.filter((f) => !f.calc && dirtyFields?.[f.name]).length;

const RedesignBody = ({
  quickFilter,
  setQuickFilter,
  footer,
  reveal,
  canReveal,
  onToggleReveal,
}) => {
  const loan = useSelector((state) => state.loanDrilldown);
  // Loan Info edit access: the Funded Date + the other closing/funding &
  // underwriting-stamped fields on this tab are OPEN TO ALL USERS by request —
  // so e.g. a processor can set the funding date required to mark a file
  // closed, without being on the hardcoded underwriter/admin roster. Backend
  // edit_permissions are advisory (never enforced server-side), so this is the
  // only gate. To re-restrict, gate on user permissions again, e.g.
  //   emailInRoles(user.emailAddress, ["underwriter","admin"]) || isAdmin || isUnderwriter
  const canEditUw = true;
  const correspondentOptions = useCorrespondentOptions();
  const isAcq = useIsAcquisitions();

  // PERF: only structural drivers are subscribed (not the whole values object).
  const { dirtyFields } = useFormState({ subscription: { dirtyFields: true } });
  const gvSub = { subscription: { value: true } };
  const productType = useField("loanProductType", gvSub).input.value;
  const purpose = useField("loanPurpose", gvSub).input.value;
  const delayedPurchase = useField(DELAYED_PURCHASE_FIELD, gvSub).input.value;
  const exitStrategyVal = useField("exitStrategy", gvSub).input.value;
  const floatingRateLoan = useField("floatingRateLoan", gvSub).input.value;
  const closingScheduled = useField("closingScheduled", gvSub).input.value;
  const termsAccepted = useField("termsAccepted", gvSub).input.value;
  const loanStatus = useField("loanStatus", gvSub).input.value;
  const closingAsEntity = useField("closingAsEntity", gvSub).input.value;
  const existingLoanInDefault = useField("existingLoanInDefault", gvSub).input
    .value;
  // Postclose drivers: expectedClosingDate drops off the Closing & Settlement
  // card once the correspondent's loan has actually closed.
  const loanClosed = useField("loanClosed", gvSub).input.value;
  const originationDate = useField("originationDate", gvSub).input.value;
  const values = useMemo(
    () => ({
      loanProductType: productType,
      loanPurpose: purpose,
      [DELAYED_PURCHASE_FIELD]: delayedPurchase,
      exitStrategy: exitStrategyVal,
      floatingRateLoan,
      closingScheduled,
      termsAccepted,
      loanStatus,
      closingAsEntity,
      existingLoanInDefault,
      loanClosed,
      originationDate,
    }),
    [
      productType,
      purpose,
      delayedPurchase,
      exitStrategyVal,
      floatingRateLoan,
      closingScheduled,
      termsAccepted,
      loanStatus,
      closingAsEntity,
      existingLoanInDefault,
      loanClosed,
      originationDate,
    ],
  );

  const q = quickFilter?.trim().toLowerCase();

  const loanSections = useMemo(
    () => buildSections(values, canEditUw, correspondentOptions, isAcq, reveal),
    [values, canEditUw, correspondentOptions, isAcq, reveal],
  );

  // Shared entity gating context (docs §8.3–8.5).
  const entityCtx = useMemo(
    () => ({
      rtl: isRTL(productType),
      dscr: isDSCR(productType),
      construction: isConstruction(productType),
      interestReserve: isInterestReserve(productType),
      purchase: isPurchasePurpose(purpose, delayedPurchase),
      refi: isRefiPurpose(purpose, delayedPurchase),
      closingAsEntity: !!closingAsEntity,
      loan,
      reveal,
    }),
    [productType, purpose, delayedPurchase, closingAsEntity, loan, reveal],
  );

  const borrowers = loan?.borrowers || [];
  const properties = loan?.subjectProperties || [];
  const borrowerSections = useMemo(
    () => borrowers.map((_, i) => buildBorrowerSections(i, entityCtx)),
    [borrowers.length, entityCtx],
  );
  const propertySections = useMemo(
    () => properties.map((_, i) => buildPropertySections(i, entityCtx)),
    [properties.length, entityCtx],
  );

  const visibleLoanSections = useMemo(
    () =>
      loanSections.filter((s) => {
        if (s.id === "reserves") return true;
        return filterRows(s.fields, q, loan).length > 0;
      }),
    [loanSections, loan, q],
  );

  const entityHasRows = (sections) =>
    sections.some(
      (s) =>
        filterRows(s.fields, q, loan).length > 0 ||
        (!!s.experience && (!q || EXPERIENCE_EDITOR_TERMS.includes(q))),
    );

  // nav groups
  const groups = useMemo(() => {
    const g = [];
    const zoneIcon = {
      terms: <GavelOutlined className="thin" />,
      pipeline: <FactCheckOutlined className="thin" />,
      more: <LinkOutlined className="thin" />,
    };
    LOAN_ZONES.forEach((zone) => {
      const zoneSections = visibleLoanSections.filter(
        (s) => s.zone === zone.id && !s.strip,
      );
      if (!zoneSections.length) return;
      g.push({
        id: `nav-zone-${zone.id}`,
        scrollId: `zone-${zone.id}`,
        label: zone.label,
        icon: zoneIcon[zone.id],
        items: zoneSections.map((s) => ({
          id: `linfo-sec-${s.id}`,
          label: s.label,
          icon: s.icon,
          dirtyCount: countDirty(s.fields, dirtyFields),
        })),
      });
    });
    const borrowerItems = borrowers
      .map((b, i) => ({ b, i }))
      .filter(({ i }) => entityHasRows(borrowerSections[i]))
      .map(({ b, i }) => ({
        id: `entity-borrower-${i}`,
        label: borrowerDisplayName(b, i),
        icon: <PeopleAltOutlined className="thin" />,
        dirtyCount: borrowerSections[i].reduce(
          (a, s) =>
            a +
            countDirty(s.fields, dirtyFields) +
            countDirtyExperience(s.experience, dirtyFields),
          0,
        ),
      }));
    if (borrowerItems.length)
      g.push({
        id: "borrowers",
        scrollId: "group-borrowers",
        label: "Borrowers",
        icon: <PeopleAltOutlined className="thin" />,
        items: borrowerItems,
      });
    const propertyItems = properties
      .map((p, i) => ({ p, i }))
      .filter(({ i }) => entityHasRows(propertySections[i]))
      .map(({ p, i }) => ({
        id: `entity-property-${i}`,
        label: propertyDisplayName(p, i),
        icon: <HomeWorkOutlined className="thin" />,
        dirtyCount: propertySections[i].reduce(
          (a, s) => a + countDirty(s.fields, dirtyFields),
          0,
        ),
      }));
    if (propertyItems.length)
      g.push({
        id: "properties",
        scrollId: "group-properties",
        label: "Properties",
        icon: <HomeWorkOutlined className="thin" />,
        items: propertyItems,
      });
    return g.map((grp) => ({
      ...grp,
      dirtyCount: grp.items.reduce((a, it) => a + it.dirtyCount, 0),
    }));
  }, [
    visibleLoanSections,
    borrowers,
    properties,
    borrowerSections,
    propertySections,
    dirtyFields,
    q,
    loan,
  ]);

  const [activeId, setActiveId] = useState(null);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const observerRef = useRef(null);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const handleSelect = (id) => {
    setActiveId(id);
    scrollTo(id);
  };
  const handleSelectGroup = (grp) => {
    scrollTo(grp.scrollId || grp.id);
    if (grp.items[0]) setActiveId(grp.items[0].id);
  };

  const cardIds = useMemo(
    () => groups.flatMap((grp) => grp.items.map((it) => it.id)),
    [groups],
  );
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    const root = document.getElementById("overflow-root") || null;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setActiveId(en.target.id);
        });
      },
      { root, rootMargin: "-90px 0px -70% 0px", threshold: 0 },
    );
    cardIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    observerRef.current = obs;
    return () => obs.disconnect();
  }, [cardIds]);

  const nothingVisible = groups.every((grp) => grp.items.length === 0);

  /* Height the grid needs to reach the bottom of the drilldown scroller (see
     the minHeight note on the grid). scrollTop compensation keeps the measured
     offset correct even when this runs mid-scroll. */
  const gridRef = useRef(null);
  const [minGridH, setMinGridH] = useState(0);
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return undefined;
    const scroller = el.closest("#overflow-root");
    const apply = () => {
      const rect = el.getBoundingClientRect();
      const top = scroller
        ? rect.top - scroller.getBoundingClientRect().top + scroller.scrollTop
        : rect.top;
      const avail =
        (scroller ? scroller.clientHeight : window.innerHeight) - top;
      setMinGridH(Math.max(0, Math.round(avail)));
    };
    apply();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(scroller || document.body);
    return () => ro.disconnect();
  }, []);

  // Masonry keyed to the card COUNT (not a fixed column width) so a zone's
  // cards expand to fill the full width instead of leaving empty columns on
  // the right. `columns: N 340px` caps at N columns but drops to fewer (never
  // narrower than ~340px) on smaller screens.
  const masonrySx = (n) => ({
    columns: `${Math.max(1, Math.min(n, 4))} 440px`,
    // columns: `${Math.max(1, Math.min(n, 4))} 540px`,
    // columns: `${Math.max(1, Math.min(n, 4))} 340px`,
    columnGap: "16px",
    "& > *": {
      breakInside: "avoid",
      WebkitColumnBreakInside: "avoid",
      width: "100%",
      mb: 2,
    },
  });
  const entityStackSx = { display: "flex", flexDirection: "column", gap: 2 };

  return (
    <Box
      ref={gridRef}
      sx={{
        // The rail paints the left column's white background + divider, and a
        // grid column only stretches to the grid's own height — so with little
        // content (a search narrowed the tab to a couple of rows) the rail
        // stopped mid-page. Fill the scroller instead. Measured, not a magic
        // number: the tab sits under a variable-height loan header + nav.
        minHeight: minGridH ? `${minGridH}px` : undefined,
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: navCollapsed ? "44px minmax(0, 1fr)" : "260px minmax(0, 1fr)",
        },
        gap: 0,
      }}
    >
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          background: "#ffffff",
          borderRight: "1px solid #e2e8f0",
        }}
      >
        {navCollapsed ? (
          <CollapsedNavRail onExpand={() => setNavCollapsed(false)} />
        ) : (
          <NestedNav
            groups={groups}
            activeId={activeId}
            onSelect={handleSelect}
            onSelectGroup={handleSelectGroup}
            quickFilter={quickFilter}
            setQuickFilter={setQuickFilter}
            onCollapse={() => setNavCollapsed(true)}
          />
        )}
      </Box>

      <Box
        sx={{
          minWidth: 0,
          pl: 3,
          pr: 3,
          pt: 4.2,
          pb: 0,
          // cap the content and center it in the (wide) right column
          width: "100%",
          maxWidth: "1880px",
          mx: "auto",
        }}
      >
        <Box sx={{ pb: 1 }}>
          <LoanTabHeader
            title="Loan Information"
            endContent={
              <Flx ac g={1.5}>
                {/* Super-admin reveal switch sits to the LEFT of the legend. */}
                {canReveal && (
                  <RevealHiddenToggle
                    reveal={reveal}
                    onToggle={onToggleReveal}
                  />
                )}
                <HorizontalFieldKey reveal={reveal} />
              </Flx>
            }
          />
        </Box>

        {nothingVisible ? (
          <Txt sx={{ opacity: 0.45, p: 3 }}>No fields match your search</Txt>
        ) : (
          <Flx column g={3.5}>
            {!q && (
              <SummaryStrip
                loan={loan}
                leverageSection={loanSections.find((s) => s.id === "leverage")}
                isAcq={isAcq}
              />
            )}
            <Flx column g={3.5}>
              {LOAN_ZONES.map((zone) => {
                const zoneSections = visibleLoanSections.filter(
                  (s) => s.zone === zone.id,
                );
                if (!zoneSections.length) return null;
                const cards = zoneSections.filter((s) => !s.strip);
                const strips = zoneSections.filter((s) => s.strip);
                return (
                  <Box key={zone.id}>
                    <ZoneHeading
                      id={`zone-${zone.id}`}
                      label={zone.label}
                      hint={zone.hint}
                    />
                    {cards.length > 0 && (
                      <Box sx={masonrySx(cards.length)}>
                        {cards.map((s) => (
                          <SectionCard
                            key={s.id}
                            section={s}
                            loan={loan}
                            quickFilter={quickFilter}
                            dirtyFields={dirtyFields}
                          />
                        ))}
                      </Box>
                    )}
                    {q &&
                      strips.map((s) => (
                        <LeverageStrip key={s.id} section={s} loan={loan} />
                      ))}
                  </Box>
                );
              })}

              {borrowers.length > 0 &&
                entityHasAny(borrowerSections, q, loan) && (
                  <Box>
                    <GroupHeading
                      id="group-borrowers"
                      icon={<PeopleAltOutlined sx={{ color: "#334155" }} />}
                      label="Borrowers"
                      count={borrowers.length}
                    />
                    <Box sx={entityStackSx}>
                      {borrowers.map((b, i) => (
                        <EntityCard
                          key={b._id || i}
                          id={`entity-borrower-${i}`}
                          title={borrowerDisplayName(b, i)}
                          subtitle={b?.emailAddress}
                          icon={<PeopleAltOutlined className="hdr-icon" />}
                          sections={borrowerSections[i]}
                          loan={loan}
                          quickFilter={quickFilter}
                          dirtyFields={dirtyFields}
                          // Lead entity open on arrival — the common case is a
                          // single borrower, and the rest stay collapsed so the
                          // initial render still skips their field trees.
                          footerContent={
                            b?._id ? (
                              <EntityDocumentsCard
                                entityType="borrower"
                                entityId={b._id}
                                entityLabel={borrowerDisplayName(b, i)}
                              />
                            ) : null
                          }
                        />
                      ))}
                    </Box>
                  </Box>
                )}

              {properties.length > 0 &&
                entityHasAny(propertySections, q, loan) && (
                  <Box>
                    <GroupHeading
                      id="group-properties"
                      icon={<HomeWorkOutlined sx={{ color: "#334155" }} />}
                      label="Properties"
                      count={properties.length}
                    />
                    <Box sx={entityStackSx}>
                      {properties.map((p, i) => (
                        <EntityCard
                          key={p._id || i}
                          id={`entity-property-${i}`}
                          title={propertyDisplayName(p, i)}
                          icon={<HomeWorkOutlined className="hdr-icon" />}
                          sections={propertySections[i]}
                          loan={loan}
                          quickFilter={quickFilter}
                          dirtyFields={dirtyFields}
                          footerContent={
                            p?._id ? (
                              <EntityDocumentsCard
                                entityType="property"
                                entityId={p._id}
                                entityLabel={propertyDisplayName(p, i)}
                              />
                            ) : null
                          }
                        />
                      ))}
                    </Box>
                  </Box>
                )}
            </Flx>
          </Flx>
        )}
        {footer}
      </Box>
    </Box>
  );
};

/* does any entity in the list have at least one row under the current search? */
const entityHasAny = (sectionsList, q, loan) =>
  sectionsList.some((sections) =>
    sections.some(
      (s) =>
        filterRows(s.fields, q, loan).length > 0 ||
        (!!s.experience && (!q || EXPERIENCE_EDITOR_TERMS.includes(q))),
    ),
  );

const FOOTER_PILL_SX = {
  borderRadius: "999px",
  textTransform: "none",
  fontSize: 12.5,
  borderColor: grey[300],
  background: "#fff",
};

/* ── super-admin-only: reveal every row that a product / purpose / value
   condition normally hides on this loan. Revealed rows render amber-tinted and
   editable, and hovering one shows the condition it is gated on. ── */
const RevealHiddenToggle = ({ reveal, onToggle }) => (
  <Tooltip
    arrow
    placement="top"
    title={
      <Box sx={{ fontSize: 11.5, lineHeight: 1.6 }}>
        <Box sx={{ fontWeight: 700 }}>Super admin only</Box>
        You can see this because your account has super-admin access. Other
        admins and teammates won&apos;t. It shows every field this loan&apos;s
        product, purpose and values would normally hide, and lets you edit them
        — hover a highlighted row to see the condition it is gated on.
      </Box>
    }
  >
    <Button
      size="small"
      variant="outlined"
      startIcon={reveal ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
      onClick={onToggle}
      sx={{
        ...FOOTER_PILL_SX,
        // both background AND color are set together — never override one alone
        ...(reveal
          ? {
              background: amber.light,
              color: amber.chipText,
              borderColor: amber.main,
              "&:hover": { background: amber.light, borderColor: amber.dark },
            }
          : null),
      }}
    >
      {reveal ? "Hide conditional fields" : "Show conditionally hidden fields"}
    </Button>
  </Tooltip>
);

/* bottom toggle — the classic/redesign swap. (The super-admin reveal switch now
   lives up top, to the left of the field-key legend.) The choice is persisted
   (partner-scoped) so it sticks across navigations. */
const ViewToggle = ({ classic, onToggle }) => (
  <Flx ac jc="center" g={1.5} sx={{ py: 2, flexWrap: "wrap" }}>
    <Button
      size="small"
      variant="outlined"
      startIcon={<SwapHorizOutlined />}
      onClick={onToggle}
      sx={FOOTER_PILL_SX}
    >
      {classic ? "Switch to new view" : "Switch to classic view"}
    </Button>
  </Flx>
);

const LoanDrilldownLoanInfoRedesignTab = () => {
  const [quickFilter, setQuickFilter] = useState("");
  // Default to the REDESIGNED Loan Info; the classic tab stays one toggle away.
  // Storage key is versioned (…V2) so users who already persisted the old
  // classic-by-default preference land on the redesign instead of being pinned
  // to classic by a stale localStorage value.
  const [classic, setClassic] = useLocalStorageState(
    nsKey("loanInfoClassicViewV2"),
    { defaultValue: false },
  );
  // SUPER-ADMIN only — revealing a conditionally-hidden row also makes it
  // editable, so this is gated tighter than the rest of the tab: normal admins
  // must not be able to edit hidden fields. The persisted flag is ANDed with the
  // permission on every render so a stale localStorage value can never reveal
  // rows for a non-super-admin.
  const { isSuperAdmin } = usePermissionsHook();
  const [revealPref, setRevealPref] = useLocalStorageState(
    nsKey("loanInfoRevealHiddenFields"),
    { defaultValue: false },
  );
  const reveal = isSuperAdmin && !classic && !!revealPref;
  const toggle = (
    <ViewToggle classic={classic} onToggle={() => setClassic((c) => !c)} />
  );

  if (classic) {
    return (
      <>
        <LoanDrilldownDetailsTab />
        {toggle}
      </>
    );
  }

  return (
    <LoanTabCanvas
      maxWidth={false}
      px={0}
      sx={{ pt: 0, pb: 0, px: "0 !important" }}
    >
      <UnifiedEditForm>
        <RedesignBody
          quickFilter={quickFilter}
          setQuickFilter={setQuickFilter}
          footer={toggle}
          reveal={reveal}
          canReveal={isSuperAdmin && !classic}
          onToggleReveal={() => setRevealPref((r) => !r)}
        />
      </UnifiedEditForm>
    </LoanTabCanvas>
  );
};

export default LoanDrilldownLoanInfoRedesignTab;
